/**
 * ReadyMade Frame PDP
 *
 * Handles:
 * - Variant / size selection → updates price, form inputs, frame mock dimensions
 * - Customise panel → border/mount/glazing overrides update mock and hidden inputs
 * - Thumbnail strip → switches between frame mock and lifestyle image overlay
 * - Related product grids → loads "More from this range" + "Similar styles" via
 *   Shopify predictive search / collection filtering
 */

(function () {
  'use strict';

  const section = document.querySelector('.readymade-frame-pdp');
  if (!section) return;

  const dataEl = document.getElementById('RMFrameProductData');
  if (!dataEl) return;

  const data = JSON.parse(dataEl.textContent);

  // Prodigi config — used at ATC time to look up _prodigi_sku for the art line item
  let prodigiConfig = [];
  try {
    const prodigiEl = document.getElementById('ProdigiConfigRMF');
    if (prodigiEl) prodigiConfig = JSON.parse(prodigiEl.textContent) || [];
  } catch {}

  function lookupProdigiConfig(sizeOption, framingOption, borderOption, artProduct) {
    if (!Array.isArray(prodigiConfig) || !prodigiConfig.length) return { warning: 'no-configuration-found' };
    const matches = prodigiConfig.filter(e =>
      e.size_option === sizeOption &&
      e.framing_option === framingOption &&
      e.border_option === borderOption &&
      e.art_product === artProduct
    );
    if (matches.length === 0) return { warning: 'no-configuration-found' };
    if (matches.length > 1) return { ...matches[0], warning: 'multiple-configurations-found' };
    return matches[0];
  }

  // i18n island — translated copy + market-aware shipping data rendered by
  // sections/readymade-frame-pdp.liquid. Liquid is the source of truth for
  // both translation and merchant-configurable values (shipping threshold
  // lives on localization.market.metafields.custom).
  const I18N = (() => {
    try {
      const node = document.getElementById('RMFrameI18n');
      if (!node) return {};
      return JSON.parse(node.textContent || '{}');
    } catch (e) {
      console.warn('[readymade-frame-pdp] i18n island missing or invalid', e);
      return {};
    }
  })();
  const T = (path, fallback) => {
    const v = path.split('.').reduce((o, k) => (o == null ? o : o[k]), I18N);
    return (v == null || v === '') ? (fallback ?? '') : v;
  };

  // Normalise sizePricing: the configurator metaobject stores it as an array
  // [{size_option:"11x14", price:{gb:"12.15",...}}, ...] — convert to keyed object.
  // Also accepts already-keyed object format {"11x14":"12.15"} for forward-compat.
  function normalizeSizePricing(raw) {
    if (!raw) return {};
    if (Array.isArray(raw)) {
      const out = {};
      raw.forEach(entry => { if (entry.size_option) out[entry.size_option] = entry.price; });
      return out;
    }
    return raw;
  }
  const sizePricing = normalizeSizePricing(data.sizePricing);

  // glazingPricing / borderPricing come pre-keyed from the metaobject:
  // { "11-x-14": { "float-glass": 0, "museum-glaze": 106.5, ... }, ... }
  const glazingPricing = data.glazingPricing || {};
  const borderPricing  = data.borderPricing  || {};

  // Determine active currency from Shopify global
  const currency = window.Shopify?.currency?.active || 'GBP';
  const marketKey = currency === 'GBP' ? 'gb'
                  : currency === 'USD' ? 'us'
                  : currency === 'EUR' ? 'eu'
                  : currency === 'AUD' ? 'au'
                  : 'default';

  function getMarketValue(entry) {
    if (!entry) return null;
    if (typeof entry === 'string') return parseFloat(entry);
    if (typeof entry === 'object') {
      const v = entry[marketKey] ?? entry['gb'] ?? entry['default'];
      return v != null ? parseFloat(v) : null;
    }
    return null;
  }

  function getPriceForSize(sizeHandle) {
    return getMarketValue(sizePricing[sizeHandle]);
  }

  function getOptionDelta(table, sizeHandle, optionHandle) {
    if (!optionHandle) return 0;
    const bySize = table[sizeHandle];
    if (!bySize) return 0;
    const val = bySize[optionHandle];
    if (val == null) return 0;
    // support multi-currency objects like sizePricing
    const num = (typeof val === 'object') ? getMarketValue(val) : parseFloat(val);
    return num != null ? num : 0;
  }

  function getTotalPrice() {
    const base = getPriceForSize(state.sizeHandle);
    if (base === null) return null;
    const glazingDelta = getOptionDelta(glazingPricing, state.sizeHandle, state.glazingHandle);
    const borderDelta  = getOptionDelta(borderPricing,  state.sizeHandle, state.borderOption);
    return base + glazingDelta + borderDelta;
  }

  // ── Art product pricing (lazy-loaded on first photo upload) ─────────────

  // null = not yet loaded; object = loaded (keyed by rm_tier size handle)
  // Each entry: { portrait: { price, artSizeHandle, artVariantId }, landscape: { ... } }
  // Both slots are always populated; consumers read through artEntryFor().
  let artPricingCache = null;
  let artPricingPromise = null;
  let hasPhoto = false;

  function getStorefrontToken() {
    return document.querySelector('meta[name="shopify-storefront-api-token"]')?.content
      || document.querySelector('[data-storefront-access-token]')?.dataset.storefrontAccessToken
      || null;
  }

  async function fetchConfiguratorGidForVariant(variantId) {
    const token = getStorefrontToken();
    if (!token) return null;
    const gid = `gid://shopify/ProductVariant/${variantId}`;
    const query = `{ node(id: "${gid}") { ... on ProductVariant { metafield(namespace: "custom", key: "configurator") { value } } } }`;
    try {
      const res = await fetch('/api/2025-07/graphql.json', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'X-Shopify-Storefront-Access-Token': token },
        body: JSON.stringify({ query }),
        signal: AbortSignal.timeout(5000),
      });
      if (!res.ok) return null;
      const json = await res.json();
      return json?.data?.node?.metafield?.value || null;
    } catch { return null; }
  }

  function loadArtPricing() {
    if (artPricingCache !== null) return Promise.resolve();
    if (artPricingPromise) return artPricingPromise;
    if (!data.artVariantsByOrientation || !Object.keys(data.artVariantsByOrientation).length) {
      artPricingCache = {};
      return Promise.resolve();
    }

    const apiOrigin = (() => {
      try { return new URL(BACKEND_HOST).origin; }
      catch { return 'https://shop-chat-agent.iamfy.tech'; }
    })();

    artPricingPromise = (async () => {
      // Collect configurator GID + variant ID for each orientation.
      // Liquid pre-renders artConfiguratorsByOrientation; fall back to a Storefront
      // API fetch for any orientation whose GID wasn't available at render time.
      const orientationOrder = ['portrait', 'landscape', 'square'];
      const configsByOrientation = {};
      for (const o of orientationOrder) {
        const variantId = data.artVariantsByOrientation[o];
        if (!variantId) continue;
        // Only a string GID is usable. If Liquid ever emits a non-string (e.g.
        // a `| json` error object), treat it as missing so the Storefront-API
        // fallback below recovers the real GID instead of poisoning the fetch.
        const rawGid = data.artConfiguratorsByOrientation?.[o];
        let gid = typeof rawGid === 'string' && rawGid ? rawGid : null;
        if (!gid) gid = await fetchConfiguratorGidForVariant(variantId);
        if (gid) configsByOrientation[o] = { gid, variantId };
      }

      const orientations = Object.keys(configsByOrientation);
      if (!orientations.length) { artPricingCache = {}; return; }

      // Fetch all orientation configurators in a single request.
      const gids = orientations.map(o => configsByOrientation[o].gid);
      console.log('[RMF] fetching sku-config for orientations:', orientations);
      const url = `${apiOrigin}/api/sku-config?gids=${encodeURIComponent(gids.join(','))}&lang=EN`;
      const r = await fetch(url, { signal: AbortSignal.timeout(8000) });
      if (!r.ok) throw new Error(`sku-config ${r.status}`);
      const json = await r.json();

      // Build a size+pricing pool per orientation.
      const pools = {};
      orientations.forEach((o, i) => {
        const config = json?.data?.[`config${i}`];
        if (!config) return;
        let rawPricing = [];
        try { rawPricing = JSON.parse(config.size_pricing?.value || '[]'); } catch {}
        pools[o] = {
          artVariantId: configsByOrientation[o].variantId,
          sizeOpts: (config.size_options?.references?.nodes || []).map(n => ({
            handle: n.handle,
            widthIn:  parseFloat(n.width_inches?.value)  || 0,
            heightIn: parseFloat(n.height_inches?.value) || 0,
          })),
          rawPricing,
        };
      });

      // For each rm_tier size, resolve the closest art size in BOTH the
      // portrait and landscape configurators — the frame can be rotated, so
      // the print orientation is decided later from state.orientation.
      artPricingCache = {};
      (data.sizeOptions || []).forEach(rmSize => {
        let w = rmSize.widthIn, h = rmSize.heightIn;
        if (!w || !h) {
          const parts = rmSize.handle.split('-x-');
          if (parts.length === 2) { w = parseFloat(parts[0]); h = parseFloat(parts[1]); }
        }
        if (!w || !h) return;

        const isSquare = Math.abs(w - h) / Math.max(w, h) < 0.05;
        const rmAspect = Math.max(w, h) / Math.min(w, h);

        // Resolve one art-size match within a given orientation pool.
        const resolve = (poolKey) => {
          const pool = pools[poolKey]
            || (isSquare ? pools['square'] : null)
            || pools['portrait']
            || Object.values(pools)[0];
          if (!pool) return null;
          let bestHandle = null, bestDiff = Infinity;
          pool.sizeOpts.forEach(a => {
            if (!a.widthIn || !a.heightIn) return;
            const aspectDiff = Math.abs(Math.max(a.widthIn, a.heightIn) / Math.min(a.widthIn, a.heightIn) - rmAspect);
            const longEdgeDiff = isSquare
              ? Math.abs(Math.max(a.widthIn, a.heightIn) - Math.max(w, h)) / Math.max(w, h)
              : 0;
            const diff = aspectDiff + longEdgeDiff;
            if (diff < bestDiff) { bestDiff = diff; bestHandle = a.handle; }
          });
          if (!bestHandle || bestDiff > 0.01) return null;
          const entry = pool.rawPricing.find(e => e.size_option === bestHandle);
          if (!entry) return null;
          const price = getMarketValue(entry.price);
          if (price === null) return null;
          return { price, artSizeHandle: bestHandle, artVariantId: pool.artVariantId };
        };

        // Squares: same product either way. Otherwise resolve each orientation.
        const portrait  = resolve(isSquare ? 'square' : 'portrait');
        const landscape = isSquare ? portrait : (resolve('landscape') || portrait);
        if (portrait || landscape) {
          artPricingCache[rmSize.handle] = {
            portrait:  portrait  || landscape,
            landscape: landscape || portrait,
          };
        }
      });

      console.log('[RMF] artPricingCache built:', artPricingCache);
    })().catch(err => {
      console.warn('[RMF] art pricing fetch failed', err);
      artPricingCache = {};
    });

    return artPricingPromise;
  }

  // Resolve the art-pricing entry for a size in the active orientation.
  function artEntryFor(sizeHandle) {
    const byOrientation = artPricingCache && artPricingCache[sizeHandle];
    if (!byOrientation) return null;
    return byOrientation[state.orientation] || byOrientation.portrait || null;
  }

  function getArtPriceForCurrentSize() {
    if (!artPricingCache || !state.sizeHandle) return null;
    return artEntryFor(state.sizeHandle)?.price ?? null;
  }

  // ── Price rendering (single source of truth) ────────────────────────────
  // Every price-writing path funnels through renderPrice(). It resolves the
  // print add-on from photo state, writes the headline / CTA / sticky price,
  // and shows or hides the breakdown. All inputs are in MAJOR units (pounds);
  // see fmt() / getTotalPrice() / getArtPriceForCurrentSize().
  function renderPrice(frameMajor, compareAtMajor) {
    if (!priceEl || frameMajor == null) return;
    // getArtPriceForCurrentSize() returns null when there's no photo-size
    // pricing yet, so a single call covers the "no print" case too.
    const printMajor = hasPhoto ? getArtPriceForCurrentSize() : null;
    const totalMajor = frameMajor + (printMajor || 0);

    const cur = priceEl.querySelector('.rmf-price__current');
    const orig = priceEl.querySelector('.rmf-price__original');
    const onSale = compareAtMajor != null && compareAtMajor > frameMajor;
    if (cur) {
      cur.className = onSale ? 'rmf-price__current rmf-price__current--sale' : 'rmf-price__current';
      cur.textContent = fmt(totalMajor);
    }
    // Strike-through "was" price includes the print too, so the absolute
    // saving reads correctly on the combined total (the discount is on the frame).
    if (orig) orig.textContent = onSale ? fmt(compareAtMajor + (printMajor || 0)) : '';

    updateCtaPrice(fmt(totalMajor));

    if (printMajor != null) renderBreakdown(printMajor, frameMajor);
    else hideBreakdown();
  }

  function renderBreakdown(printMajor, frameMajor) {
    const el = priceEl && priceEl.querySelector('#rmf-price-breakdown');
    if (!el) return;
    const printEl = el.querySelector('[data-rmf-breakdown-print]');
    const frameEl = el.querySelector('[data-rmf-breakdown-frame]');
    if (printEl) printEl.textContent = `${fmt(printMajor)} print`;
    if (frameEl) frameEl.textContent = `${fmt(frameMajor)} ${data.frameLabel || 'frame'}`;
    el.hidden = false;
  }

  function hideBreakdown() {
    const el = priceEl && priceEl.querySelector('#rmf-price-breakdown');
    if (el) el.hidden = true;
  }

  function refreshPriceDisplay() {
    renderPrice(getTotalPrice(), null);
  }

  // Determine whether we're in tier-mode (size_options from configurator)
  // or variant-mode (product.variants with per-size SKUs).
  // Fall back to DOM check in case sizeOptions JSON is empty but buttons rendered.
  const isTierMode = (data.sizeOptions && data.sizeOptions.length > 0)
    || document.querySelector('.rmf-size-btn[data-size-handle]') !== null;

  // Indexed by handle for O(1) lookup when switching colours
  const frameColorMap = {};
  (data.frameColorOptions || []).forEach(fc => { frameColorMap[fc.handle] = fc; });

  // Tiles are extracted from the right edge (inner→outer left-to-right) but the
  // 3D bundle expects outer→inner. Pre-flip each tile and cache a Promise so
  // async callers (boot3D, refreshRoomPreview) can await resolution before
  // building the payload — avoiding the race where a cached bundle mounts before
  // the image flip callback fires.
  const flippedTilePromiseCache = new Map(); // originalUrl → Promise<resolvedDataUrl>
  const flippedTileUrlCache     = new Map(); // originalUrl → resolvedDataUrl (sync after resolved)
  function flipTileAsync(url) {
    if (!url) return Promise.resolve(null);
    if (flippedTilePromiseCache.has(url)) return flippedTilePromiseCache.get(url);
    const p = new Promise(resolve => {
      const img = new Image();
      img.crossOrigin = 'anonymous';
      img.onload = () => {
        try {
          const c = document.createElement('canvas');
          c.width = img.width; c.height = img.height;
          const cx = c.getContext('2d');
          cx.translate(img.width, 0); cx.scale(-1, 1);
          cx.drawImage(img, 0, 0);
          const dataUrl = c.toDataURL();
          flippedTileUrlCache.set(url, dataUrl);
          resolve(dataUrl);
        } catch (_) { flippedTileUrlCache.set(url, url); resolve(url); }
      };
      img.onerror = () => { flippedTileUrlCache.set(url, url); resolve(url); };
      img.src = url;
    });
    flippedTilePromiseCache.set(url, p);
    return p;
  }
  // Kick off flips for all frame colours up front so they're ready before 3D mounts.
  (data.frameColorOptions || []).forEach(fc => { if (fc.mouldingTile) flipTileAsync(fc.mouldingTile); });

  // Glazing + mount-color catalogues are curated on the configurator metaobject
  // (rm_tier_obj.glazing_options / mount_color_options). First entry = default.
  // If the preset's glazing/mount handle isn't in the curated list, snap to first.
  const _glazingCatalogue = data.glazingOptions || [];
  const _mountColorCatalogue = data.mountColorOptions || [];
  const _glazingHandles = _glazingCatalogue.map(g => g.handle);
  const _mountColorHandles = _mountColorCatalogue.map(m => m.handle);
  const _initialGlazing = _glazingHandles.includes(data.glazingHandle)
    ? data.glazingHandle
    : (_glazingHandles[0] || data.glazingHandle);
  const _initialMountColor = _mountColorHandles.includes(data.mountColorHandle)
    ? data.mountColorHandle
    : (_mountColorHandles[0] || data.mountColorHandle);

  let state = {
    variantId: data.variants.find(v => v.available)?.id || data.variants[0]?.id,
    sizeHandle: null,  // set on first size button click (tier mode)
    orientation: 'portrait',  // 'portrait' | 'landscape' — derived from the uploaded photo
    borderOption: data.borderOption,
    glazingHandle: _initialGlazing,
    mountColorHandle: _initialMountColor,
    mountColorInnerHandle: _initialMountColor,  // default inner = same as outer
    frameColorHandle: data.frameColorHandle,
  };

  // Returns the active frame-color config object (falling back to page-data defaults)
  function currentFrameColorData() {
    return frameColorMap[state.frameColorHandle] || null;
  }

  function getFrameProp(key) {
    return currentFrameColorData()?.[key] ?? data[key];
  }

  // 3D room-preview state (declared early so refreshRoomPreview() can safely
  // early-return during the module-init selectSizeOption() call before the
  // user has interacted with the "In your room" thumb).
  const roomPreviewState = {
    bundleReady: null,       // Promise<void>
    analysis: null,          // cached /api/room-analysis response
    instance: null,          // { update, dispose }
    lastPayload: null,       // last payload passed to mount/update (incl. _sig)
    showingRoom: false,      // true when the real room composite is displayed
    flowAbort: null,         // AbortController — aborts a superseded startRoomFlow
  };

  // Artwork-photo state for the 3D preview. Kept in sync with the 2D mock
  // via the photo-upload:changed / :removed DOM events (see farther down).
  // `url` is a blob:/data:/http URL; mutated by applyPhotoToMock.
  const currentArtwork = { url: null, aspect: null };

  // ── Frame orientation ───────────────────────────────────────────────────
  // A readymade frame is orientation-agnostic: a portrait 16×20 and a
  // landscape 20×16 are the same physical product hung the other way. The
  // rm_tier size metaobjects are all portrait; we flip client-side based on
  // the uploaded photo's aspect ratio.

  function isLandscape() { return state.orientation === 'landscape'; }

  // Paper dims are always parsed portrait-first from the size handle
  // ("16x20" → w=16, h=20). Return them in the active orientation.
  function orientDims(w, h) {
    const lo = Math.min(w, h), hi = Math.max(w, h);
    return isLandscape() ? { w: hi, h: lo } : { w: lo, h: hi };
  }

  // Apply a new orientation: update state, mirror it onto <body> for any
  // CSS hooks, and re-run every surface that renders paper dimensions.
  function applyOrientation(next) {
    const o = next === 'landscape' ? 'landscape' : 'portrait';
    if (o === state.orientation) return;
    state.orientation = o;
    document.body.dataset.frameOrientation = o;
    updateSizeButtonLabels();
    updateSizeHeader();
    if (state.sizeHandle) selectSizeOption(state.sizeHandle);
    populateSizePrices();
    requestAnimationFrame(updateStickyChrome);
    const heroActiveBtn = document.querySelector('#rmf-size-buttons .rmf-size-btn--active');
    if (heroActiveBtn) renderHeroSizeChips(heroActiveBtn);
  }

  // In tier mode, read default size from the active button; fall back to first button
  if (isTierMode) {
    const activeSizeBtn = document.querySelector('.rmf-size-btn--active[data-size-handle]')
      || document.querySelector('.rmf-size-btn[data-size-handle]');
    if (activeSizeBtn) {
      activeSizeBtn.classList.add('rmf-size-btn--active');
      activeSizeBtn.setAttribute('aria-selected', 'true');
      state.sizeHandle = activeSizeBtn.dataset.sizeHandle;
    }
  }
  document.body.dataset.frameOrientation = state.orientation;

  // ── Catalogues (sourced from the configurator metaobject; ordering preserved) ──

  const MOUNT_COLORS = _mountColorCatalogue.map(mc => ({
    handle: mc.handle,
    hex: mc.hex,
    name: mc.name,
    coreHex: mc.coreHex || null,
    mountType: mc.mountType || 'standard',
  }));

  const GLAZING_OPTIONS = _glazingCatalogue.map(g => ({
    handle: g.handle,
    name: g.name,
    desc: g.description || '',
  }));

  // ── Elements ────────────────────────────────────────────────────────────────

  const frameMock        = document.getElementById('rmf-frame-mock');
  const heroImg          = document.getElementById('rmf-hero-img');
  const heroCss3d        = document.getElementById('rmf-hero-css3d');
  const thumbMockCanvas  = document.getElementById('rmf-thumb-mock-canvas');
  const thumbRoomCanvas  = document.getElementById('rmf-thumb-room-canvas');
  const stickyThumbCanvas = document.getElementById('rmf-sticky-thumb-canvas');
  const priceEl          = document.getElementById('rmf-price');
  const sizeBtns         = document.querySelectorAll('.rmf-size-btn');
  const sizeLabelEl      = document.getElementById('rmf-size-label');
  const frameInput       = document.getElementById('rmf-frame-variant-input');
  const propFrameColor   = document.getElementById('rmf-prop-frame-color');
  const colorSummaryEl   = document.getElementById('rmf-color-summary');
  const colorIconSwatch  = document.getElementById('rmf-color-icon-swatch');
  const frameColorOptionsEl = document.getElementById('rmf-frame-color-options');
  const propBorder       = document.getElementById('rmf-prop-border');
  const propGlazing      = document.getElementById('rmf-prop-glazing');
  const propMount             = document.getElementById('rmf-prop-mount');
  const propMountInner        = document.getElementById('rmf-prop-mount-inner');
  const mountColorField       = document.getElementById('rmf-mount-color-field');
  const mountInnerColorField  = document.getElementById('rmf-mount-inner-color-field');
  const mountColorPills       = document.getElementById('rmf-mount-color-pills');
  const mountInnerColorPills  = document.getElementById('rmf-mount-inner-color-pills');
  const mountOuterLabel       = document.getElementById('rmf-mount-outer-label');
  const mountColorNameEl      = document.getElementById('rmf-mount-color-name-head');
  const mountInnerColorNameEl = document.getElementById('rmf-mount-inner-color-name-head');
  const mountSummaryEl        = document.getElementById('rmf-mount-summary');
  const glazingSummaryEl  = document.getElementById('rmf-glazing-summary');
  const glazingCardsEl    = document.getElementById('rmf-glazing-cards');
  const photoSummaryEl    = document.getElementById('rmf-photo-summary');
  const photoStackRow     = document.getElementById('rmf-stack-photo');
  const sizeIconEl        = document.getElementById('rmf-size-icon');
  const mockHintBtn       = document.getElementById('rmf-mock-hint');
  const lifestyleOverlay  = document.getElementById('rmf-lifestyle-overlay');
  const lifestyleImg      = document.getElementById('rmf-lifestyle-img');
  const thumbs            = document.querySelectorAll('.rmf-thumb');
  const mockWrap          = document.getElementById('rmf-mock-wrap');
  const roomPreviewEl     = document.getElementById('rmf-room-preview');
  const roomPreviewStage  = document.getElementById('rmf-room-preview-stage');
  const roomPreviewStatus = document.getElementById('rmf-room-preview-status');
  const roomPreviewInput  = document.getElementById('rmf-room-preview-input');
  const roomPreviewBtn    = document.getElementById('rmf-room-preview-btn');

  // ── Size / variant selection ─────────────────────────────────────────────

  // Use the Shopify-configured money_format from the data island so the
  // decimal style (comma vs period) matches the rest of the storefront
  // (e.g. €60,85 in EU markets, not €60.85). Falls back to Intl when the
  // money_format placeholder is missing.
  const _moneyFormat = (typeof data.moneyFormat === 'string' && data.moneyFormat) || '';
  const _formatAmount = (cents, opts) => {
    const value = (cents || 0) / 100;
    const useComma = !!(opts && opts.comma);
    const useSpace = !!(opts && opts.space);
    const noDecimals = !!(opts && opts.noDecimals);
    const fixed = noDecimals
      ? Math.round(value).toString()
      : value.toFixed(2);
    let [intPart, decPart] = fixed.split('.');
    const groupSep = useSpace ? ' ' : (useComma ? '.' : ',');
    intPart = intPart.replace(/\B(?=(\d{3})+(?!\d))/g, groupSep);
    if (noDecimals || decPart == null) return intPart;
    const decSep = useComma ? ',' : '.';
    return intPart + decSep + decPart;
  };
  const fmt = p => {
    if (!_moneyFormat) {
      try {
        return new Intl.NumberFormat(document.documentElement.lang || 'en-GB', {
          style: 'currency',
          currency: window.Shopify?.currency?.active || 'GBP',
          minimumFractionDigits: 2,
          maximumFractionDigits: 2,
        }).format(p);
      } catch (e) {
        return (window.Shopify?.currency?.active || 'GBP') + ' ' + (p || 0).toFixed(2);
      }
    }
    // p is in major units (e.g. dollars/euros), so convert back to cents
    // for the placeholder substitution helper.
    const cents = Math.round((p || 0) * 100);
    return _moneyFormat.replace(/\{\{\s*(\w+)\s*\}\}/g, (_, key) => {
      switch (key) {
        case 'amount':
          return _formatAmount(cents);
        case 'amount_no_decimals':
          return _formatAmount(cents, { noDecimals: true });
        case 'amount_with_comma_separator':
          return _formatAmount(cents, { comma: true });
        case 'amount_no_decimals_with_comma_separator':
          return _formatAmount(cents, { comma: true, noDecimals: true });
        case 'amount_with_space_separator':
          return _formatAmount(cents, { space: true });
        case 'amount_no_decimals_with_space_separator':
          return _formatAmount(cents, { space: true, noDecimals: true });
        case 'amount_with_apostrophe_separator':
          return _formatAmount(cents).replace(/,/g, '\'');
        default:
          return _formatAmount(cents);
      }
    });
  };

  function selectSizeOption(sizeHandle) {
    state.sizeHandle = sizeHandle;
    document.dispatchEvent(new CustomEvent('rmf:size-changed', { detail: { handle: sizeHandle, productId: data.productId } }));

    sizeBtns.forEach(btn => {
      const active = btn.dataset.sizeHandle === sizeHandle;
      btn.classList.toggle('rmf-size-btn--active', active);
      btn.setAttribute('aria-selected', String(active));
      if (active) {
        const dim = formatBtnDim(btn, displayUnit);
        // Section header value reads "{Bucket} ({size}) {price}" per design.
        // The bucket name comes from the active bucket button; the price
        // comes from the per-size slot (already computed in populateSizePrices).
        const bucket = btn.dataset.bucket;
        const bucketBtn = bucket ? document.querySelector(`.rmf-bucket[data-bucket="${bucket}"] .rmf-bucket__name`) : null;
        const bucketName = bucketBtn ? bucketBtn.textContent.trim() : '';
        const headerLabel = bucketName ? `${bucketName} (${dim})` : dim;
        if (sizeLabelEl) sizeLabelEl.textContent = headerLabel;
        if (sizeIconEl) sizeIconEl.textContent = dim;
      }
    });

    refreshPriceDisplay();
    populateMountPrices();
    updatePhotoRowDelta();

    updateSizeProp(sizeHandle);
    renderGlazingCards();

    // Artwork dimensions for 2D mock — prefer the size handle ("18x24" → 18×24)
    // as the paper dimension. The metaobject's `width_in` / `width_cms` fields
    // are inconsistent across tiers (some store paper, others store visible
    // area), whereas the handle always represents the paper size.
    const activeBtn = document.querySelector(`.rmf-size-btn[data-size-handle="${sizeHandle}"]`);
    if (activeBtn && frameMock) {
      const parts = String(sizeHandle).split(/[x×-]/).map(Number).filter(n => n > 0);
      let w = parts[0];
      let h = parts[1];
      if (!w || !h) {
        w = parseFloat(activeBtn.dataset.widthIn) || 16;
        h = parseFloat(activeBtn.dataset.heightIn) || w;
      }
      const od = orientDims(w, h);
      frameMock.updateConfig?.({ artworkWidth: od.w, artworkAspect: od.w / od.h });
    }
    refreshRoomPreview();
  }

  function selectVariant(variantId) {
    const v = data.variants.find(x => x.id === variantId);
    if (!v) return;
    state.variantId = variantId;

    sizeBtns.forEach(btn => {
      const active = Number(btn.dataset.variantId) === variantId;
      btn.classList.toggle('rmf-size-btn--active', active);
      btn.setAttribute('aria-selected', String(active));
    });

    if (sizeLabelEl) sizeLabelEl.textContent = v.title;
    if (sizeIconEl) sizeIconEl.textContent = v.title;
    if (frameInput) frameInput.value = variantId;
    updatePriceFromCents(v.price, v.compareAtPrice);
    updateMockSizeFromTitle(v.title);

    const url = new URL(window.location.href);
    url.searchParams.set('variant', variantId);
    window.history.replaceState({}, '', url);
  }

  function updateCtaPrice(text) {
    const ctaPrice = document.getElementById('rmf-cta-price');
    if (ctaPrice) ctaPrice.textContent = text;
    const stickyPrice = document.getElementById('rmf-sticky-price');
    if (stickyPrice) stickyPrice.textContent = text;
  }

  function updatePriceFromValue(pounds) {
    renderPrice(pounds, null);
  }

  function updatePriceFromCents(cents, compareAtCents) {
    renderPrice(cents / 100, compareAtCents != null ? compareAtCents / 100 : null);
  }

  function updateMockSizeFromTitle(title) {
    if (!frameMock) return;
    const map = {
      '11x14': 11, '12x12': 12, '12x16': 12, '16x16': 16, '16x20': 16,
      '16x24': 16, '18x24': 18, '20x20': 20, '20x24': 20, '20x28': 20,
      '20x30': 20, '24x24': 24, '24x32': 24, '26x26': 26,
    };
    const key = title.toLowerCase().replace(/[^0-9x]/g, '');
    const w = map[key] || 16;
    const parts = key.split('x');
    const h = parts.length === 2 ? (parseFloat(parts[1]) || w) : w;
    frameMock.updateConfig?.({ artworkWidth: w, artworkAspect: w / h });
    refreshRoomPreview();
  }

  // Hidden input for size property (used in ATC form for tier-mode)
  let sizePropInput = document.getElementById('rmf-prop-size');
  if (!sizePropInput && isTierMode) {
    sizePropInput = document.createElement('input');
    sizePropInput.type = 'hidden';
    sizePropInput.name = 'properties[_size_option]';
    sizePropInput.id = 'rmf-prop-size';
    const form = document.getElementById('rmf-form-frame');
    if (form) form.appendChild(sizePropInput);
  }

  function updateSizeProp(sizeHandle) {
    if (sizePropInput) sizePropInput.value = sizeHandle;
  }

  // Populate per-size prices into button slots, plus the "From £X" label
  // on each bucket card (cheapest size in that bucket — stays stable as
  // the customer browses, per the design handover).
  function populateSizePrices() {
    const bucketMins = { nook: Infinity, accent: Infinity, focal: Infinity, statement: Infinity };
    document.querySelectorAll('.rmf-size-btn[data-size-handle]').forEach(btn => {
      const handle = btn.dataset.sizeHandle;
      const base   = getPriceForSize(handle);
      if (base === null) return;
      let total = base
        + getOptionDelta(glazingPricing, handle, state.glazingHandle)
        + getOptionDelta(borderPricing,  handle, state.borderOption);
      if (hasPhoto && artPricingCache) {
        const artEntry = artEntryFor(handle);
        if (artEntry) total += artEntry.price;
      }
      const slot = btn.querySelector('[data-price-slot]');
      if (slot) slot.textContent = fmt(total);

      // Track the minimum total per bucket for the "From £X" label.
      const bucket = btn.dataset.bucket;
      if (bucket && total < bucketMins[bucket]) bucketMins[bucket] = total;
    });

    // Write the bucket "From" prices into the bucket card price slots.
    Object.entries(bucketMins).forEach(([bucket, min]) => {
      const slot = document.querySelector(`[data-bucket-price="${bucket}"]`);
      if (!slot) return;
      slot.textContent = Number.isFinite(min) ? fmt(min) : '';
    });
  }

  function populateMountPrices() {
    // Render prices relative to the current selection so each option's label
    // reflects what choosing it would *add* (or save) — matches how the user
    // mentally evaluates upgrades.
    const currentDelta = getOptionDelta(borderPricing, state.sizeHandle || '', state.borderOption);
    document.querySelectorAll('.rmf-mount-option[data-mount]').forEach(btn => {
      const slot = btn.querySelector('[data-mount-price]');
      if (!slot) return;
      const absDelta = getOptionDelta(borderPricing, state.sizeHandle || '', btn.dataset.mount);
      const delta = absDelta - currentDelta;
      if (delta === 0) {
        slot.textContent = '';
      } else if (delta > 0) {
        slot.textContent = `+${fmt(delta)}`;
      } else {
        slot.textContent = fmt(delta);
      }
    });
  }

  function updatePhotoRowDelta() {
    const hintEl = document.getElementById('rmf-photo-price-hint');
    const artPrice = getArtPriceForCurrentSize();
    if (hintEl) hintEl.textContent = artPrice !== null ? `+${fmt(artPrice)} ` : '';
  }

  sizeBtns.forEach(btn => {
    btn.addEventListener('click', () => {
      if (btn.classList.contains('rmf-size-btn--aspect-mismatch')) return;
      if (btn.classList.contains('rmf-size-btn--resolution-blocked')) return;
      if (isTierMode) {
        selectSizeOption(btn.dataset.sizeHandle);
      } else {
        if (btn.dataset.variantAvailable === 'false') return;
        selectVariant(Number(btn.dataset.variantId));
      }
    });
  });

  // Inject quality label span into each tier-mode size button (reserves vertical space
  // so all buttons stay the same height whether or not a quality label is shown)
  sizeBtns.forEach(btn => {
    if (btn.dataset.sizeHandle && !btn.querySelector('.rmf-size-btn__quality')) {
      const span = document.createElement('span');
      span.className = 'rmf-size-btn__quality';
      btn.appendChild(span);
    }
  });

  // ── IN/CM unit toggle ────────────────────────────────────────────────────
  // Source-of-truth precedence (matches the art PDP via assets/pdp-size-picker.js
  // + assets/sku-picker.js):
  //   1. localStorage['pdp_unit']             ← cross-PDP user pick
  //   2. localStorage['skuPicker_unitPreference']  ← legacy key
  //   3. .rmf-unit-toggle[data-default-unit]  ← market default (server-rendered)
  //   4. 'in'                                 ← final fallback
  function readUnitPref() {
    try {
      const pdp = localStorage.getItem('pdp_unit');
      if (pdp === 'in' || pdp === 'cm') return pdp;
      const legacy = localStorage.getItem('skuPicker_unitPreference');
      if (legacy === 'in' || legacy === 'cm') return legacy;
    } catch {}
    const toggle = document.querySelector('.rmf-unit-toggle');
    const seedFromMarkup = toggle?.dataset?.defaultUnit;
    if (seedFromMarkup === 'in' || seedFromMarkup === 'cm') return seedFromMarkup;
    return 'in';
  }
  function writeUnitPref(u) {
    try {
      localStorage.setItem('pdp_unit', u);
      // Mirror the legacy key so the art PDP's sku-picker sees it too —
      // the user's choice carries across both PDP types.
      localStorage.setItem('skuPicker_unitPreference', u);
    } catch {}
  }
  let displayUnit = readUnitPref(); // state is always stored in inches; this only affects display

  function formatBtnDim(btn, unit) {
    let wIn = parseFloat(btn.dataset.widthIn);
    let hIn = parseFloat(btn.dataset.heightIn);
    let wCm = parseFloat(btn.dataset.widthCm);
    let hCm = parseFloat(btn.dataset.heightCm);
    // Frame is orientation-agnostic — display dims in the active orientation.
    if (wIn > 0 && hIn > 0) { const d = orientDims(wIn, hIn); wIn = d.w; hIn = d.h; }
    if (wCm > 0 && hCm > 0) { const d = orientDims(wCm, hCm); wCm = d.w; hCm = d.h; }
    if (unit === 'cm') {
      if (wCm > 0 && hCm > 0) return `${Math.round(wCm)} × ${Math.round(hCm)}cm`;
      if (wIn > 0 && hIn > 0) return `${Math.round(wIn * 2.54)} × ${Math.round(hIn * 2.54)}cm`;
    } else {
      if (wIn > 0 && hIn > 0) return `${Math.round(wIn)} × ${Math.round(hIn)}″`;
      if (wCm > 0 && hCm > 0) return `${Math.round(wCm / 2.54)} × ${Math.round(hCm / 2.54)}″`;
    }
    return formatRawSize(btn.dataset.sizeLabel || btn.dataset.sizeHandle);
  }

  // Reformat a raw size handle ("20x28") or label into "20 × 28″".
  // No-op when the input is already formatted.
  function formatRawSize(raw) {
    if (!raw) return '';
    const m = /^\s*(\d+(?:\.\d+)?)\s*[x×]\s*(\d+(?:\.\d+)?)\s*("|″|in)?\s*$/i.exec(raw);
    if (!m) return raw;
    return `${m[1]} × ${m[2]}″`;
  }

  function updateSizeButtonLabels() {
    document.querySelectorAll('.rmf-size-btn[data-size-handle]').forEach(btn => {
      const dimEl = btn.querySelector('.rmf-size-btn__dim');
      if (!dimEl) return;
      dimEl.textContent = formatBtnDim(btn, displayUnit);
    });
  }

  function updateSizeHeader() {
    const activeBtn = document.querySelector('.rmf-size-btn--active[data-size-handle]');
    if (!activeBtn) return;
    const dim = formatBtnDim(activeBtn, displayUnit);
    const bucket = activeBtn.dataset.bucket;
    const bucketBtn = bucket ? document.querySelector(`.rmf-bucket[data-bucket="${bucket}"] .rmf-bucket__name`) : null;
    const bucketName = bucketBtn ? bucketBtn.textContent.trim() : '';
    const headerLabel = bucketName ? `${bucketName} (${dim})` : dim;
    if (sizeLabelEl) sizeLabelEl.textContent = headerLabel;
    if (sizeIconEl) sizeIconEl.textContent = dim;
  }

  // Wrap the label text node in each tier-mode size button so we can swap it
  document.querySelectorAll('.rmf-size-btn[data-size-handle]').forEach(btn => {
    if (!btn.querySelector('.rmf-size-btn__dim')) {
      const textNode = Array.from(btn.childNodes).find(
        n => n.nodeType === Node.TEXT_NODE && n.textContent.trim()
      );
      if (textNode) {
        const span = document.createElement('span');
        span.className = 'rmf-size-btn__dim';
        span.textContent = formatBtnDim(btn, displayUnit);
        btn.replaceChild(span, textNode);
      }
    }
  });

  // Apply the resolved unit to the UI. Two phases:
  //   - paintToggleAndLabels(): pure-DOM updates (toggle active class,
  //     bucket-row data-unit attr, size button labels + header). Safe to
  //     call at init time before later-declared helpers exist.
  //   - applyUnit(): wraps the above plus measure-overlay sync. Used in
  //     the click handler (fires well after init) so the measure-overlay
  //     reflects the new unit while it's visible.
  function paintToggleAndLabels(next) {
    if (next !== 'in' && next !== 'cm') return;
    displayUnit = next;
    document.querySelectorAll('.rmf-unit-toggle__btn').forEach(b => {
      b.classList.toggle('rmf-unit-toggle__btn--active', b.dataset.unit === displayUnit);
    });
    const bucketRowEl = document.getElementById('rmf-bucket-row');
    if (bucketRowEl) bucketRowEl.dataset.unit = displayUnit;
    updateSizeButtonLabels();
    updateSizeHeader();
    // Re-render banner dim string in the new unit if already visible
    const _banner = document.getElementById('rmf-size-quality-banner');
    if (_banner && !_banner.hidden) applyResolutionFilter();
  }

  function applyUnit(next) {
    paintToggleAndLabels(next);
    if (typeof measuresVisible !== 'undefined' && measuresVisible) renderMeasureOverlay();
    if (typeof syncMeasurements3d === 'function') syncMeasurements3d();
  }

  document.querySelectorAll('.rmf-unit-toggle__btn').forEach(btn => {
    btn.addEventListener('click', () => {
      const next = btn.dataset.unit;
      if (next === displayUnit) return;
      applyUnit(next);
      writeUnitPref(next);
      // Notify the art PDP / sku-picker so the unit stays in sync when
      // the user crosses between PDP types in the same session.
      document.dispatchEvent(new CustomEvent('sku-picker:unit-changed', {
        detail: { unit: next },
      }));
    });
  });

  // Receive cross-PDP unit changes (e.g. the surgical PDP's <pdp-size-picker>
  // emits this on flip). Re-apply if it differs from current.
  document.addEventListener('sku-picker:unit-changed', (e) => {
    const next = e?.detail?.unit;
    if (next && next !== displayUnit) applyUnit(next);
  });

  // Apply the initial read on load so the bucket-row data-unit attr and any
  // labels rendered before this script ran reflect the resolved preference.
  // Uses paintToggleAndLabels so it doesn't touch later-declared measure helpers.
  paintToggleAndLabels(displayUnit);

  // Run on load
  populateSizePrices();
  populateMountPrices();
  if (isTierMode && state.sizeHandle) {
    selectSizeOption(state.sizeHandle);
  }
  // Eagerly fetch art pricing so the photo row delta is visible before upload
  if (data.artConfiguratorGid) {
    loadArtPricing().then(() => updatePhotoRowDelta());
  }

  // ── Shared size picker bridge ────────────────────────────────────────────
  // The visible UI is rendered by <pdp-size-picker>; the legacy buttons
  // (kept as a hidden source-of-truth) drive everything else. Bridge mirrors
  // state in both directions.
  const sizePickerEl = document.getElementById('rmf-size-picker');
  if (sizePickerEl && isTierMode) {
    const _computeTotal = (handle) => {
      const base = getPriceForSize(handle);
      if (base === null) return null;
      let total = base
        + getOptionDelta(glazingPricing, handle, state.glazingHandle)
        + getOptionDelta(borderPricing,  handle, state.borderOption);
      if (hasPhoto && artPricingCache) {
        const artEntry = artEntryFor(handle);
        if (artEntry) total += artEntry.price;
      }
      return total;
    };

    const _readBtnState = (btn) => {
      const handle = btn.dataset.sizeHandle;
      if (!handle) return null;
      const wIn = parseFloat(btn.dataset.widthIn) || 0;
      const hIn = parseFloat(btn.dataset.heightIn) || 0;
      const wCm = parseFloat(btn.dataset.widthCm) || 0;
      const hCm = parseFloat(btn.dataset.heightCm) || 0;
      // Frame is orientation-agnostic — feed the picker dims in the active orientation.
      const inDims = orientDims(wIn || (wCm ? wCm / 2.54 : 0), hIn || (hCm ? hCm / 2.54 : 0));
      const cmDims = orientDims(wCm || (wIn ? Math.round(wIn * 2.54) : 0), hCm || (hIn ? Math.round(hIn * 2.54) : 0));
      const qualityEl = btn.querySelector('.rmf-size-btn__quality');
      const qualityText = qualityEl ? qualityEl.textContent.trim() : '';
      return {
        handle,
        name: btn.dataset.sizeLabel || handle,
        width_inches:  inDims.w,
        height_inches: inDims.h,
        width_cms:     cmDims.w,
        height_cms:    cmDims.h,
        price:          _computeTotal(handle),
        blocked:        btn.classList.contains('rmf-size-btn--resolution-blocked')
                     || btn.classList.contains('rmf-size-btn--aspect-mismatch'),
        warning:        btn.classList.contains('rmf-size-btn--resolution-marginal'),
        soldOut:        btn.classList.contains('rmf-size-btn--sold-out'),
        qualityLabel:   qualityText,
      };
    };

    // Use the host's currency formatter so totals match the rest of the page.
    sizePickerEl.formatPrice = (v) => (typeof v === 'number' ? fmt(v) : '');

    // Bucket vocabulary from the RMF i18n island — without this the shared
    // component renders its built-in English names on every locale.
    sizePickerEl.buckets = [
      { id: 'XS', name: T('bucket.nook', 'Nook'),           desc: T('bucket.desc_nook', '') },
      { id: 'S',  name: T('bucket.accent', 'Accent'),       desc: T('bucket.desc_accent', '') },
      { id: 'M',  name: T('bucket.focal', 'Focal'),         desc: T('bucket.desc_focal', '') },
      { id: 'L',  name: T('bucket.statement', 'Statement'), desc: T('bucket.desc_statement', '') },
    ];
    sizePickerEl.popularLabel = T('bucket.popular', 'Popular');

    const syncPickerFromDom = () => {
      const btns = document.querySelectorAll('.rmf-size-btn[data-size-handle]');
      const opts = [...btns].map(_readBtnState).filter(Boolean);
      sizePickerEl.options = opts;
      const activeBtn = document.querySelector('.rmf-size-btn--active[data-size-handle]')
                     || document.querySelector('.rmf-size-btn[data-size-handle]');
      if (activeBtn) sizePickerEl.value = activeBtn.dataset.sizeHandle;
      // Keep the unit pref in sync — picker reads from localStorage at
      // boot but the active rmf unit toggle is the authoritative state.
      const activeUnit = document.querySelector('.rmf-unit-toggle__btn--active');
      if (activeUnit?.dataset.unit) sizePickerEl.unit = activeUnit.dataset.unit;
    };

    // Picker → legacy: clicks on the picker fire clicks on the matching
    // legacy button so all existing handlers (selectSizeOption, hero size
    // chip sync, sticky CTA refresh) run unchanged.
    sizePickerEl.addEventListener('pdp-size-picker:change', (e) => {
      const handle = e.detail?.handle;
      if (!handle) return;
      const btn = document.querySelector(`.rmf-size-btn[data-size-handle="${handle}"]`);
      if (btn && !btn.disabled) btn.click();
    });
    sizePickerEl.addEventListener('pdp-size-picker:unit-change', (e) => {
      const u = e.detail?.unit;
      if (!u) return;
      const target = document.querySelector(`.rmf-unit-toggle__btn[data-unit="${u}"]`);
      if (target && !target.classList.contains('rmf-unit-toggle__btn--active')) target.click();
    });

    // Legacy → picker: any class/text mutation in the size area triggers a
    // resync. Covers selectSizeOption, populateSizePrices, applyResolution
    // Filter, applySizeAspectFilter, applyBucketDisabledFilter, etc.
    const sizeButtonsHost = document.getElementById('rmf-size-buttons');
    if (sizeButtonsHost) {
      const mo = new MutationObserver(() => {
        // Coalesce bursts of mutations into one resync per frame.
        if (sizePickerEl._rmfBridgeRaf) return;
        sizePickerEl._rmfBridgeRaf = requestAnimationFrame(() => {
          sizePickerEl._rmfBridgeRaf = null;
          syncPickerFromDom();
        });
      });
      mo.observe(sizeButtonsHost, {
        subtree: true,
        attributes: true,
        attributeFilter: ['class', 'aria-selected', 'aria-disabled', 'title'],
        characterData: true,
        childList: true,
      });
    }

    syncPickerFromDom();
  }

  // ── Config stack ─────────────────────────────────────────────────────────

  // Mat widths in cm — differentiated so the canvas mock visually reflects
  // the user's mount choice. Deep mounts use a wider border (6cm) to convey
  // recess; doubles match their single equivalent (the inner mat colour
  // surfaces the difference instead of width).
  const MOUNT_BORDER_WIDTHS = {
    'single-mount': 4, 'deep-mount': 6,
    'double-mount': 4, 'double-deep-mount': 6,
  };
  const DOUBLE_MOUNT_TYPES = new Set(['double-mount', 'double-deep-mount']);
  const NO_COLOR_MOUNT_TYPES = new Set(['no-mount', 'mirror']);

  function updateMockBorder(handle) {
    if (!frameMock) return;
    const innerHex = MOUNT_COLORS.find(c => c.handle === state.mountColorInnerHandle)?.hex;
    frameMock.updateConfig?.({
      borderOption: handle,
      borderWidth: MOUNT_BORDER_WIDTHS[handle] || 0,
      ...(DOUBLE_MOUNT_TYPES.has(handle) && innerHex ? { mountColorInner: innerHex } : {}),
    });
    // The pre-baked hero raster only shows the preset's default mount;
    // once the user switches mount type we need to reveal the live canvas
    // so they actually see the change. Same trick as selectFrameColor's
    // no-baked-mock fallback path.
    revealCanvasMock();
  }

  function revealCanvasMock() {
    // When 3D is active, the scene owns the display — refreshRoomPreview()
    // handles live option updates. Don't hide heroImg here: the 3D canvas
    // renders with a transparent backdrop and relies on heroImg showing through
    // for the wall background.
    if (mockWrap?.classList.contains('rmf-mock-wrap--room-mode')) return;
    if (frameMock) frameMock.classList.remove('rmf-frame-mock--behind-hero');
    if (heroImg) heroImg.style.visibility = 'hidden';
  }

  function updateMockMount(hex) {
    if (!frameMock) return;
    if (hex) frameMock.updateConfig?.({ mountColor: hex });
    revealCanvasMock();
  }

  function updateMountIcon(mountType) {
    const icon = document.getElementById('rmf-mount-icon');
    if (!icon) return;
    const diag = icon.querySelector('.rmf-mount-diagram');
    if (!diag) return;
    const cls = mountType === 'double-mount' ? 'double'
              : mountType === 'single-mount' ? 'single'
              : mountType === 'deep-mount' ? 'deep'
              : mountType === 'double-deep-mount' ? 'double-deep'
              : mountType === 'mirror' ? 'mirror'
              : 'none';
    diag.className = `rmf-mount-diagram rmf-mount-diagram--${cls}`;
  }

  // Flat-open layout: every config row renders permanently open (matches the
  // 2026-04-28 PDP redesign). Headers are decorative — no toggle, no chevron.
  const configStack = document.getElementById('rmf-config-stack');
  if (configStack) {
    configStack.querySelectorAll('.rmf-stack-row__header').forEach(header => {
      header.setAttribute('aria-expanded', 'true');
      const body = document.getElementById(header.getAttribute('aria-controls'));
      if (body) body.hidden = false;
    });
  }

  // Heart save (wishlist toggle, visual-only — wishlist persistence is wired
  // separately in the global theme; this just flips the pressed state).
  const ctaHeart = document.getElementById('rmf-cta-heart');
  if (ctaHeart) {
    ctaHeart.addEventListener('click', () => {
      const pressed = ctaHeart.getAttribute('aria-pressed') === 'true';
      ctaHeart.setAttribute('aria-pressed', pressed ? 'false' : 'true');
    });
  }

  // Bucket-row size selector. Filters the size grid by [data-bucket] and,
  // when the user picks a new bucket, snaps the active size to the middle
  // of that bucket's options (matches handoff §7b).
  const bucketRow = document.getElementById('rmf-bucket-row');
  const sizeButtonsEl = document.getElementById('rmf-size-buttons');
  if (bucketRow && sizeButtonsEl) {
    // Hide bucket row entirely if only one bucket has options (sparse tiers).
    const visibleBuckets = bucketRow.querySelectorAll('.rmf-bucket:not([hidden])');
    if (visibleBuckets.length <= 1) {
      bucketRow.dataset.onlyOne = 'true';
    }

    const selectMiddleOfBucket = bucketId => {
      const sizesInBucket = sizeButtonsEl.querySelectorAll(`.rmf-size-btn[data-bucket="${bucketId}"]`);
      if (!sizesInBucket.length) return;
      const target = sizesInBucket[Math.floor(sizesInBucket.length / 2)];
      // Only fire if it's not already the active size.
      if (!target.classList.contains('rmf-size-btn--active')) target.click();
    };

    bucketRow.querySelectorAll('.rmf-bucket').forEach(btn => {
      btn.addEventListener('click', () => {
        const bucketId = btn.dataset.bucket;
        if (!bucketId) return;
        if (btn.classList.contains('rmf-bucket--disabled')) return;
        bucketRow.querySelectorAll('.rmf-bucket').forEach(b => b.classList.remove('rmf-bucket--active'));
        btn.classList.add('rmf-bucket--active');
        sizeButtonsEl.dataset.activeBucket = bucketId;
        selectMiddleOfBucket(bucketId);
      });
    });
  }

  // Mount "+ See all 6 mount types" — one-way reveal of the more tier.
  // Once expanded the button hides itself (CSS handles the visibility).
  const mountSeeMoreBtn = document.getElementById('rmf-mount-seemore');
  const mountOptionsEl = document.getElementById('rmf-mount-options');
  if (mountSeeMoreBtn && mountOptionsEl) {
    mountSeeMoreBtn.addEventListener('click', () => {
      mountOptionsEl.dataset.showMore = 'true';
      mountSeeMoreBtn.setAttribute('aria-expanded', 'true');
    });
  }

  // ── Mode tabs (Frame / Your wall) ──────────────────────────────────────────
  // Drive what the hero stage renders. Both modes use the 3D scene container
  // (rmf-mock-wrap--room-mode stays on so the canvas mock and hero raster
  // stay hidden once the bundle has loaded). The difference is the payload:
  //   • Frame: synthetic-wall preview (orbit the frame in space)
  //   • Wall:  room composite once a wall photo has been uploaded + analysed.
  //            Pre-upload, clicking the wall tab opens the file picker instead
  //            of switching the stage (the wall tab IS the upload affordance);
  //            the spinner state lives on the same segment during analysis.
  const modeTabs = document.getElementById('rmf-mode-tabs');
  // Both the legacy below-hero strip (.rmf-mode-tab) and the new in-hero
  // overlay (.pdp-mode-tab, shared with the surgical PDP) live in the DOM —
  // only one is visible at a time per breakpoint, but we sync both groups
  // when either fires so toggling at one breakpoint keeps state consistent.
  const allModeTabSelectors = '.rmf-mode-tab, .pdp-mode-tab';
  if (mockWrap && document.querySelector(allModeTabSelectors)) {
    const heroRotateChip = document.querySelector('.rmf-hero-rotate-chip');

    const setMode = mode => {
      document.querySelectorAll(allModeTabSelectors).forEach(t => {
        const active = t.dataset.mode === mode;
        t.classList.toggle('rmf-mode-tab--active', active);
        t.classList.toggle('pdp-mode-tab--active', active);
        t.setAttribute('aria-selected', active ? 'true' : 'false');
      });

      mockWrap.dataset.heroMode = mode;
      if (roomPreviewEl) roomPreviewEl.setAttribute('aria-hidden', 'false');

      // Rotate chip is only meaningful in frame mode (orbit the synthetic wall).
      if (heroRotateChip) heroRotateChip.style.display = mode === 'frame' ? '' : 'none';

      // No instance yet — the 3D bundle hasn't booted. Trigger boot3D so it
      // picks up the current heroMode + analysis state and mounts the right
      // payload. Without this, a cross-PDP "click Your wall" before any
      // other intent gesture would just set the hero-mode CSS and leave the
      // user looking at the static raster.
      if (!roomPreviewState.instance) {
        boot3D();
        return;
      }

      if (mode === 'frame') {
        if (!roomPreviewState.lastPayload?._default) {
          mountRoomPreview(buildDefaultPayload()).catch(() => {});
        }
      } else if (mode === 'wall' && roomPreviewState.analysis) {
        if (roomPreviewState.lastPayload?._default) {
          mountRoomPreview(buildMountPayload(roomPreviewState.analysis)).catch(() => {});
        }
      }
    };

    document.querySelectorAll(allModeTabSelectors).forEach(tab => {
      tab.addEventListener('click', () => {
        // Pre-upload, the wall tab IS the upload entry point. Open the file
        // picker directly so the user doesn't get parked on an empty stage.
        // Once a wall photo has been analysed, fall through to normal mode
        // switching.
        if (tab.dataset.mode === 'wall'
          && !roomPreviewState.analysis
          && !tab.classList.contains('pdp-mode-tab--busy')
          && roomPreviewInput) {
          roomPreviewInput.click();
          return;
        }
        setMode(tab.dataset.mode);
      });
    });

    // Change-photo affordance inside the hero pill — visible only once a
    // wall photo has been mounted (CSS-gated by .pdp-mode-tabs--has-wall).
    const changePhotoBtn = document.getElementById('rmf-hero-change-photo');
    if (changePhotoBtn && roomPreviewInput) {
      changePhotoBtn.addEventListener('click', () => roomPreviewInput.click());
    }

    // Hero meta caption (the row below the hero — "3D model · 1 / 4" etc).
    const heroCaption = document.getElementById('rmf-hero-caption');
    const HERO_CAPTIONS = {
      frame: '3D model · 1 / 4',
      wall: T('gallery.living_room_wall', 'Living room · 8 ft wall'),
    };
    const updateHeroCaption = mode => {
      if (heroCaption) heroCaption.textContent = HERO_CAPTIONS[mode] || HERO_CAPTIONS.frame;
    };

    // [data-mode-target] elements outside the tab strip (hero meta links,
    // hero "Try in your room" action) drive the same mode switcher.
    document.querySelectorAll('[data-mode-target]').forEach(el => {
      el.addEventListener('click', () => {
        const m = el.dataset.modeTarget;
        if (m) {
          setMode(m);
          updateHeroCaption(m);
        }
      });
    });

    // Fire caption update on every tab click in either group.
    document.addEventListener('click', e => {
      const t = e.target.closest(allModeTabSelectors);
      if (t) requestAnimationFrame(() => updateHeroCaption(t.dataset.mode));
    });
  }

  // Hero meta size chip — keep in sync with active size button.
  // Updates both the imperial chip ("12 × 16\"") and the metric companion
  // ("30×40 CM") to match target's caption row format.
  const heroSizeChip = document.getElementById('rmf-hero-size-chip');
  const heroSizeChipCm = document.getElementById('rmf-hero-size-chip-cm');

  // Render both hero chips from a size button, in the active orientation.
  function renderHeroSizeChips(btn, { onlyIfEmpty = false } = {}) {
    if (!btn) return;
    if (heroSizeChip && !(onlyIfEmpty && heroSizeChip.textContent.trim())) {
      heroSizeChip.textContent = formatBtnDim(btn, 'in');
    }
    if (heroSizeChipCm) {
      const wcm = parseInt(btn.dataset.widthCm, 10);
      const hcm = parseInt(btn.dataset.heightCm, 10);
      if (wcm > 0 && hcm > 0) {
        const d = orientDims(wcm, hcm);
        heroSizeChipCm.textContent = `${Math.round(d.w)}×${Math.round(d.h)} CM`;
      }
    }
  }

  if (heroSizeChip || heroSizeChipCm) {
    document.getElementById('rmf-size-buttons')?.addEventListener('click', e => {
      const btn = e.target.closest('.rmf-size-btn');
      if (!btn) return;
      requestAnimationFrame(() => renderHeroSizeChips(btn));
    });

    // Initial render: derive cm + label from the default-active size button.
    // (`default_size_label` is assigned later in the Liquid template than the
    // hero chip, so the chip ships empty — pre-fill from JS at boot.)
    const activeBtn = document.querySelector('#rmf-size-buttons .rmf-size-btn--active');
    if (activeBtn) renderHeroSizeChips(activeBtn, { onlyIfEmpty: true });
  }

  // Hero "Save · N" — wishlist toggle (visual only; production wires real wishlist).
  const heroSaveBtn = document.getElementById('rmf-hero-save-btn');
  const heroSaveCount = document.getElementById('rmf-hero-save-count');
  if (heroSaveBtn && heroSaveCount) {
    heroSaveBtn.addEventListener('click', () => {
      const isSaved = heroSaveBtn.dataset.saved === 'true';
      const next = !isSaved;
      heroSaveBtn.dataset.saved = String(next);
      const current = parseInt(heroSaveCount.textContent, 10) || 0;
      heroSaveCount.textContent = String(current + (next ? 1 : -1));
    });
  }

  // ── Sticky CTA footer ──────────────────────────────────────────────────────
  // Visible when the in-page primary CTA scrolls out of view. The sticky
  // button submits the existing #rmf-form-frame so analytics + variant tracking
  // stay consistent — we don't duplicate the add-to-cart form.
  const stickyCta = document.getElementById('rmf-sticky-cta');
  const stickyBtn = document.getElementById('rmf-sticky-add-btn');
  const inPageCta = document.getElementById('rmf-buy-frame-btn');
  const stickyForm = document.getElementById('rmf-form-frame');
  const stickyColorEl = document.getElementById('rmf-sticky-color-name');
  const stickySizeEl = document.getElementById('rmf-sticky-size-label');
  const stickyBucketEl = document.getElementById('rmf-sticky-bucket');
  const stickyMountEl = document.getElementById('rmf-sticky-mount-name');
  const stickyPriceEl = document.getElementById('rmf-sticky-price');

  const BUCKET_LABEL = {
    nook:      T('bucket.nook',      'Nook'),
    accent:    T('bucket.accent',    'Accent'),
    focal:     T('bucket.focal',     'Focal'),
    statement: T('bucket.statement', 'Statement'),
  };
  const MOUNT_LABEL = {
    'no-mount':          T('mount.no-mount',          'No mount'),
    'single-mount':      T('mount.single-mount',      'Single mount'),
    'double-mount':      T('mount.double-mount',      'Double mount'),
    'deep-mount':        T('mount.deep-mount',        'Deep mount'),
    'double-deep-mount': T('mount.double-deep-mount', 'Double deep mount'),
    'mirror':            T('mount.mirror',            'Mirror mount'),
  };

  if (stickyCta && inPageCta && 'IntersectionObserver' in window) {
    const cIO = new IntersectionObserver(entries => {
      const [entry] = entries;
      // Show the bar when the in-page CTA is fully out of view (below or above).
      stickyCta.setAttribute('aria-hidden', entry.isIntersecting ? 'true' : 'false');
    }, { threshold: 0 });
    cIO.observe(inPageCta);
  }

  if (stickyBtn && stickyForm) {
    stickyBtn.addEventListener('click', () => {
      // Submit via the in-page form so the existing handler (variant tracking,
      // analytics, cart-drawer open) takes over.
      stickyForm.requestSubmit ? stickyForm.requestSubmit() : stickyForm.submit();
    });
  }

  // Live-update sticky labels in lockstep with the in-page state.
  const stickyColorWrap = document.getElementById('rmf-sticky-color-wrap');
  const stickyMountWrap = document.getElementById('rmf-sticky-mount-wrap');
  const stickyPriceCompare = document.getElementById('rmf-sticky-price-compare');
  const updateStickyChrome = () => {
    const activeSize = document.querySelector('.rmf-size-btn--active');
    if (stickySizeEl && activeSize) stickySizeEl.textContent = formatBtnDim(activeSize, displayUnit);
    if (stickyBucketEl && activeSize) {
      const bucket = activeSize.dataset.bucket;
      stickyBucketEl.textContent = BUCKET_LABEL[bucket] || '';
    }
    if (stickyMountEl) {
      const activeMount = document.querySelector('.rmf-mount-option--active');
      const mountKey = activeMount?.dataset.mount;
      if (mountKey) stickyMountEl.textContent = MOUNT_LABEL[mountKey] || '';
      // Hide the ", <mount>" wrap entirely on no-mount selections so the
      // sub line reads cleanly as "<bucket> <size>, <color> · <price>".
      if (stickyMountWrap) stickyMountWrap.hidden = !mountKey || mountKey === 'no-mount';
    }
    if (stickyColorEl && colorSummaryEl) {
      const colorTxt = colorSummaryEl.textContent.trim();
      stickyColorEl.textContent = colorTxt;
      if (stickyColorWrap) stickyColorWrap.hidden = !colorTxt;
    }
    if (stickyPriceEl) {
      const liveCurrent = document.querySelector('.rmf-price__current');
      if (liveCurrent) stickyPriceEl.textContent = liveCurrent.textContent.trim();
    }
    if (stickyPriceCompare) {
      const liveCompare = document.querySelector('.rmf-price__compare, .rmf-price__original');
      const cmp = liveCompare?.textContent.trim() || '';
      const cur = stickyPriceEl?.textContent.trim() || '';
      if (cmp && cmp !== cur) {
        stickyPriceCompare.textContent = cmp;
        stickyPriceCompare.hidden = false;
      } else {
        stickyPriceCompare.textContent = '';
        stickyPriceCompare.hidden = true;
      }
    }
    // Sticky thumb mirrors the live frame mock — no manual swatch sync needed
    // (frameMock.addThumbnailTarget(stickyThumbCanvas) handles it on every render).
  };
  // Wire updates onto the existing config events (cheap delegated listener).
  document.getElementById('rmf-config-stack')?.addEventListener('click', () => {
    requestAnimationFrame(updateStickyChrome);
  });
  // Initial paint
  requestAnimationFrame(updateStickyChrome);

  // ── Floating Advisor chip auto-hide ────────────────────────────────────────
  // Mobile: hide the float when the in-page advisor card is at least 40% visible.
  // Desktop (≥1024px) keeps the chip pinned per the desktop spec — there's
  // more screen real estate so it doesn't crowd the config column.
  const floatWrap = document.getElementById('rmf-float-wrap');
  const advisorAnchor = document.querySelector('[data-rmf-advisor-anchor]');
  if (floatWrap && advisorAnchor && 'IntersectionObserver' in window) {
    const isDesktop = () => window.matchMedia('(min-width: 1024px)').matches;
    const io = new IntersectionObserver(entries => {
      const [entry] = entries;
      if (isDesktop()) {
        floatWrap.setAttribute('aria-hidden', 'false');
        return;
      }
      floatWrap.setAttribute('aria-hidden', entry.isIntersecting ? 'true' : 'false');
    }, { threshold: 0.4 });
    io.observe(advisorAnchor);
    // Re-evaluate on viewport changes (mobile→desktop without reload).
    window.addEventListener('resize', () => {
      if (isDesktop()) floatWrap.setAttribute('aria-hidden', 'false');
    });
  }

  // Mount option icon buttons. Section-header summary shows just the mount
  // type ("Single", "Double") — the mat color lives in its own section below
  // so we don't compound it into the mount header.
  function updateMountSummary() {
    if (!mountSummaryEl) return;
    const type = state.borderOption;
    const MOUNT_SHORT = {
      'single-mount':      T('mount.short-single',      'Single'),
      'double-mount':      T('mount.short-double',      'Double'),
      'deep-mount':        T('mount.short-deep',        'Deep'),
      'double-deep-mount': T('mount.short-double-deep', 'Double deep'),
      'mirror':            T('mount.short-mirror',      'Mirror'),
    };
    if (!type || type === 'no-mount') { mountSummaryEl.textContent = T('mount.no-mount', 'No mount'); return; }
    mountSummaryEl.textContent = MOUNT_SHORT[type] || type;
  }

  document.querySelectorAll('.rmf-mount-option').forEach(btn => {
    btn.addEventListener('click', () => {
      const val = btn.dataset.mount;
      state.borderOption = val;
      if (propBorder) propBorder.value = val;
      document.dispatchEvent(new CustomEvent('rmf:border-changed', { detail: { handle: val, productId: data.productId } }));
      updateMockBorder(val);
      populateSizePrices();
      populateMountPrices();
      refreshPriceDisplay();
      document.querySelectorAll('.rmf-mount-option').forEach(b => b.classList.remove('rmf-mount-option--active'));
      btn.classList.add('rmf-mount-option--active');
      const isDouble = DOUBLE_MOUNT_TYPES.has(val);
      if (mountColorField) mountColorField.hidden = NO_COLOR_MOUNT_TYPES.has(val);
      if (mountInnerColorField) mountInnerColorField.hidden = !isDouble;
      if (mountOuterLabel) mountOuterLabel.textContent = isDouble ? T('colors.outer_mat', 'Outer Mount Colour') : T('colors.mat_color', 'Mount Colour');
      if (propMountInner) propMountInner.disabled = !isDouble;
      updateMountIcon(val);
      updateMountSummary();
      updateSpecsCard();
      // Re-render glazing cards so their relative deltas stay accurate after
      // a border/mount option change (border pricing affects the base).
      renderGlazingCards();
      // Mirror = no photo possible; hide photo row and mock hint
      if (photoStackRow) photoStackRow.hidden = (val === 'mirror');
      if (mockHintBtn) mockHintBtn.hidden = (val === 'mirror');
      refreshRoomPreview();
    });
  });

  function buildMountPills(container, activeHandle, onSelect) {
    if (!container) return;
    container.innerHTML = '';
    // QA #20: the catalogue contains regular + "deep" variants of each colour
    // sharing the same name & hex (e.g. mount-bright-white / mount-deep-bright-white),
    // which surfaced as visually-identical duplicate pills. Mount depth is chosen
    // via the mount-type selector, not the colour, so collapse same-name+hex
    // entries to one pill here. MOUNT_COLORS is left intact for hex/label lookups.
    const activeColor = MOUNT_COLORS.find(c => c.handle === activeHandle);
    const colourKey = mc => ((mc.name || '') + '|' + (mc.hex || '')).toLowerCase();
    const activeKey = activeColor ? colourKey(activeColor) : null;
    const seen = new Set();
    MOUNT_COLORS.forEach(mc => {
      const key = colourKey(mc);
      if (seen.has(key)) return;
      seen.add(key);
      const isActive = mc.handle === activeHandle || (activeKey && key === activeKey);
      const btn = document.createElement('button');
      btn.type = 'button';
      btn.className = 'rmf-colour-pill' + (isActive ? ' rmf-colour-pill--active' : '');
      btn.style.background = mc.hex;
      btn.title = mc.name;
      btn.setAttribute('aria-label', mc.name);
      btn.addEventListener('click', () => {
        container.querySelectorAll('.rmf-colour-pill').forEach(p => p.classList.remove('rmf-colour-pill--active'));
        btn.classList.add('rmf-colour-pill--active');
        onSelect(mc);
      });
      container.appendChild(btn);
    });
  }

  // Format the mat color label as "Name · NN" where NN is the 1-based,
  // zero-padded index in the curated palette.
  function matLabel(mc) {
    if (!mc) return '';
    const idx = MOUNT_COLORS.findIndex(c => c.handle === mc.handle);
    if (idx < 0) return mc.name;
    const num = String(idx + 1).padStart(2, '0');
    return `${mc.name} · ${num}`;
  }

  // Outer mount colour pills
  buildMountPills(mountColorPills, state.mountColorHandle, mc => {
    state.mountColorHandle = mc.handle;
    if (propMount) propMount.value = mc.handle;
    updateMockMount(mc.hex);
    updateMountSummary();
    if (mountColorNameEl) mountColorNameEl.textContent = matLabel(mc);
    refreshRoomPreview();
  });

  // Inner mount colour pills (only relevant for double mount)
  buildMountPills(mountInnerColorPills, state.mountColorInnerHandle, mc => {
    state.mountColorInnerHandle = mc.handle;
    if (propMountInner) propMountInner.value = mc.handle;
    if (frameMock) frameMock.updateConfig?.({ mountColorInner: mc.hex });
    revealCanvasMock();
    updateMountSummary();
    if (mountInnerColorNameEl) mountInnerColorNameEl.textContent = matLabel(mc);
    refreshRoomPreview();
  });

  // Seed initial mat color name in the section head ("Soft Cream · #01" style)
  {
    const initialOuter = MOUNT_COLORS.find(c => c.handle === state.mountColorHandle);
    if (mountColorNameEl && initialOuter) mountColorNameEl.textContent = matLabel(initialOuter);
    const initialInner = MOUNT_COLORS.find(c => c.handle === state.mountColorInnerHandle);
    if (mountInnerColorNameEl && initialInner) mountInnerColorNameEl.textContent = matLabel(initialInner);
  }

  // Glazing list
  function updateGlazingSummary() {
    if (!glazingSummaryEl) return;
    const opt = GLAZING_OPTIONS.find(o => o.handle === state.glazingHandle);
    glazingSummaryEl.textContent = opt ? opt.name : (state.glazingHandle || '');
  }

  function renderGlazingCards() {
    if (!glazingCardsEl) return;
    // Bug 1 fix: compute deltas relative to the currently-selected glazing so
    // the shown delta always answers "how much more/less than what I have now?"
    const activeDelta = getOptionDelta(glazingPricing, state.sizeHandle || '', state.glazingHandle);
    glazingCardsEl.innerHTML = '';
    // Show prices relative to the current selection, not the base price —
    // so the active glazing reads "Included" and the others show what they
    // would *add* (or save) versus the user's current pick.
    GLAZING_OPTIONS.forEach(opt => {
      const absDelta = getOptionDelta(glazingPricing, state.sizeHandle || '', opt.handle);
      const relDelta = absDelta - activeDelta;
      const isActive = opt.handle === state.glazingHandle;
      const priceText = isActive
        ? 'Selected'
        : relDelta === 0 ? 'Same price'
        : relDelta > 0 ? `+${fmt(relDelta)}`
        : fmt(relDelta);
      const tagHtml = opt.tag
        ? `<span class="rmf-tag rmf-tag--${opt.tag}">${opt.tag === 'included' ? T('tag.included', 'Included') : T('tag.popular', 'Popular')}</span>`
        : '';
      const btn = document.createElement('button');
      btn.type = 'button';
      btn.className = 'rmf-glazing-option' + (isActive ? ' rmf-glazing-option--active' : '');
      btn.setAttribute('role', 'radio');
      btn.setAttribute('aria-checked', String(isActive));
      btn.innerHTML = `
        <span class="rmf-glazing-option__radio"></span>
        <span class="rmf-glazing-option__swatch"></span>
        <span class="rmf-glazing-option__body">
          <span class="rmf-glazing-option__name">${opt.name}${tagHtml}</span>
          <span class="rmf-glazing-option__desc">${opt.desc}</span>
        </span>
        <span class="rmf-glazing-option__price">${priceText}</span>`;
      btn.addEventListener('click', () => {
        state.glazingHandle = opt.handle;
        if (propGlazing) propGlazing.value = opt.handle;
        document.dispatchEvent(new CustomEvent('rmf:glazing-changed', { detail: { handle: opt.handle, glazingName: opt.name || opt.handle, productId: data.productId } }));
        populateSizePrices();
        populateMountPrices();
        refreshPriceDisplay();
        updateGlazingSummary();
        renderGlazingCards();
        updateSpecsCard();
        refreshRoomPreview();
      });
      glazingCardsEl.appendChild(btn);
    });
  }

  // ── Specifications card ─────────────────────────────────────────────────
  // Reads the current state and updates each row's <dd>; rows whose source
  // metafield is empty are hidden via the `hidden` attribute. Profile pulls
  // moulding width from the active frame color; depth is preset-level so
  // it only changes if the framing_option swaps. Material/Finish read from
  // frameColorMap; Glazing description from data.glazingOptions; Mat
  // description from data.borderDescriptions (also hidden for no-mount).
  const _glazingDescByHandle = {};
  (data.glazingOptions || []).forEach(g => {
    if (g.description) _glazingDescByHandle[g.handle] = g.description;
  });
  const _borderDescByHandle = data.borderDescriptions || {};

  function setSpecRow(rowId, ddId, value) {
    const row = document.getElementById(rowId);
    const dd = document.getElementById(ddId);
    if (!row || !dd) return;
    const v = (value || '').trim();
    if (!v) {
      row.hidden = true;
    } else {
      dd.textContent = v;
      row.hidden = false;
    }
  }

  function updateSpecsCard() {
    const fc = currentFrameColorData() || {};
    const widthMm = fc.mouldingWidth ?? data.mouldingWidth ?? 22;
    const depthMm = data.profileDepthMm || 32;
    const profileEl = document.getElementById('rmf-spec-profile');
    if (profileEl) profileEl.textContent = `${widthMm} mm wide × ${depthMm} mm deep`;

    setSpecRow('rmf-spec-row-material', 'rmf-spec-material', fc.materialLabel || '');
    setSpecRow('rmf-spec-row-finish',   'rmf-spec-finish',   fc.finishLabel   || '');
    setSpecRow('rmf-spec-row-glazing',  'rmf-spec-glazing',  _glazingDescByHandle[state.glazingHandle] || '');

    // Mat row — hide entirely when no mount; otherwise look up the
    // border_option description by handle.
    const matRow = document.getElementById('rmf-spec-row-mat');
    if (matRow) {
      if (state.borderOption === 'no-mount' || !state.borderOption) {
        matRow.hidden = true;
      } else {
        setSpecRow('rmf-spec-row-mat', 'rmf-spec-mat', _borderDescByHandle[state.borderOption] || '');
      }
    }
  }

  // Wire up the "Compare glazing types" toggle (inline expandable table below
  // the glazing options).
  const glazingCompareBtn = document.getElementById('rmf-glazing-compare-btn');
  const glazingCompareTable = document.getElementById('rmf-glazing-compare-table');
  if (glazingCompareBtn && glazingCompareTable) {
    glazingCompareBtn.addEventListener('click', () => {
      const isOpen = glazingCompareBtn.getAttribute('aria-expanded') === 'true';
      glazingCompareBtn.setAttribute('aria-expanded', String(!isOpen));
      glazingCompareTable.hidden = isOpen;
    });

    // Filter and reorder comparison table to match available SKU options
    const availableHandles = GLAZING_OPTIONS.map(g => g.handle);
    const tbody = document.getElementById('rmf-glazing-compare-tbody');
    if (tbody) {
      const rowsByHandle = {};
      tbody.querySelectorAll('tr[data-glazing]').forEach(row => {
        rowsByHandle[row.dataset.glazing] = row;
        row.hidden = true; // hide all initially
      });
      // Append rows in SKU order, showing only available ones
      availableHandles.forEach(handle => {
        const row = rowsByHandle[handle];
        if (row) {
          row.hidden = false;
          tbody.appendChild(row);
        }
      });
    }
  }

  // Mock hint → jump to photo row
  if (mockHintBtn) {
    mockHintBtn.addEventListener('click', () => {
      const photoHeader = document.querySelector('#rmf-stack-photo .rmf-stack-row__header');
      if (photoHeader && photoHeader.getAttribute('aria-expanded') !== 'true') {
        photoHeader.click();
      }
      document.getElementById('rmf-config-stack')?.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
    });
  }

  // Register thumbnail canvases to receive a blit after every main render.
  // The gallery thumb + sticky-CTA thumb both mirror the live frame mock,
  // so frame color, mount, and size changes propagate without extra work.
  if (frameMock) {
    if (thumbMockCanvas) frameMock.addThumbnailTarget?.(thumbMockCanvas);
    if (stickyThumbCanvas) frameMock.addThumbnailTarget?.(stickyThumbCanvas);
  }

  // Init: sync border from active mount button → frame mock
  const activeMountBtn = document.querySelector('.rmf-mount-option--active');
  const initialBorder = activeMountBtn ? activeMountBtn.dataset.mount : state.borderOption;
  state.borderOption = initialBorder;
  if (frameMock) {
    frameMock.updateConfig?.({
      borderOption: initialBorder,
      borderWidth: MOUNT_BORDER_WIDTHS[initialBorder] || 0,
      mountColor: data.mountColorHex || '#fafaf8',
    });
  }
  if (mountColorField) mountColorField.hidden = NO_COLOR_MOUNT_TYPES.has(initialBorder);
  if (mountInnerColorField) mountInnerColorField.hidden = !DOUBLE_MOUNT_TYPES.has(initialBorder);
  if (mountOuterLabel) mountOuterLabel.textContent = DOUBLE_MOUNT_TYPES.has(initialBorder) ? T('colors.outer_mat', 'Outer Mount Colour') : T('colors.mat_color', 'Mount Colour');
  if (propMountInner) propMountInner.disabled = !DOUBLE_MOUNT_TYPES.has(initialBorder);
  if (photoStackRow) photoStackRow.hidden = (initialBorder === 'mirror');
  if (mockHintBtn) mockHintBtn.hidden = (initialBorder === 'mirror');

  renderGlazingCards();
  updateMountSummary();
  updateGlazingSummary();
  updateSpecsCard();

  // ── Frame colour picker ───────────────────────────────────────────────────

  function renderFrameColorOptions() {
    if (!frameColorOptionsEl) return;
    frameColorOptionsEl.innerHTML = '';

    // Tile lineup: render every frame color, tag the first 6 as `popular`
    // and the rest as `more`. CSS hides `more` until the see-more button
    // flips `data-show-more="true"`. If the active color sits beyond the
    // popular slice, hoist it into the last popular slot so it stays visible
    // before the user expands the picker.
    const all = data.frameColorOptions || [];
    const MAX_POPULAR = 6;
    let ordered = all.slice();
    const activeIdx = ordered.findIndex(fc => fc.handle === state.frameColorHandle);
    if (activeIdx >= MAX_POPULAR) {
      const [active] = ordered.splice(activeIdx, 1);
      ordered.splice(MAX_POPULAR - 1, 0, active);
    }

    ordered.forEach((fc, i) => {
      const isActive = fc.handle === state.frameColorHandle;
      const btn = document.createElement('button');
      btn.type = 'button';
      btn.className = 'rmf-frame-color-btn' + (isActive ? ' rmf-frame-color-btn--active' : '');
      btn.dataset.colorHandle = fc.handle;
      btn.dataset.tier = i < MAX_POPULAR ? 'popular' : 'more';
      btn.setAttribute('role', 'option');
      btn.setAttribute('aria-selected', String(isActive));
      btn.setAttribute('aria-label', fc.name);

      // Flat frame-face swatch — the moulding tile (wood grain, lacquer, etc.)
      // or hex fallback fills the full swatch area. CSS inner shadow lifts
      // it off the card for depth.
      const swatchStyle = fc.mouldingTile
        ? `background-image:url('${fc.mouldingTile}');background-color:${fc.hex || '#14110d'};`
        : `background-color:${fc.hex || '#14110d'};`;

      // Bug 4 fix: the header already shows the selected colour name via
      // rmf-color-summary, and the description line repeats it — the swatch
      // label under each button is redundant.  Keep only the swatch.
      btn.innerHTML = `
        <span class="rmf-frame-color-btn__swatch" style="${swatchStyle}"></span>`;

      btn.addEventListener('click', () => selectFrameColor(fc.handle));
      frameColorOptionsEl.appendChild(btn);
    });
  }

  // Frame color "View all N frame colors" — bidirectional reveal/collapse.
  const colorSeeMoreBtn = document.getElementById('rmf-color-seemore');
  if (colorSeeMoreBtn && frameColorOptionsEl) {
    const lblMore = colorSeeMoreBtn.querySelector('.rmf-color-seemore__label--more');
    const lblLess = colorSeeMoreBtn.querySelector('.rmf-color-seemore__label--less');
    colorSeeMoreBtn.addEventListener('click', () => {
      const expanded = colorSeeMoreBtn.getAttribute('aria-expanded') === 'true';
      const next = !expanded;
      colorSeeMoreBtn.setAttribute('aria-expanded', String(next));
      frameColorOptionsEl.dataset.showMore = next ? 'true' : 'false';
      if (lblMore) lblMore.hidden = next;
      if (lblLess) lblLess.hidden = !next;
    });
  }

  // Mood/finish descriptors used when fc.description is empty on the
  // metaobject. Keyed by frame_color handle (lowercased, hyphenated) and
  // by name token (lowercased, normalized) so it matches both pure-* and
  // shared finishes like "natural-oak", "sage-green", "matte-black", etc.
  const FRAME_COLOR_DESCRIPTORS = {
    // Tones / finishes
    black: 'Bold, classic',
    'matte-black': 'Bold, classic',
    'soft-white': 'Clean, contemporary',
    white: 'Clean, contemporary',
    'brushed-white': 'Soft, contemporary',
    'natural-oak': 'Warm, organic',
    oak: 'Warm, organic',
    walnut: 'Rich, traditional',
    'dark-walnut': 'Rich, traditional',
    'sage-green': 'Muted, contemporary',
    sage: 'Muted, contemporary',
    green: 'Calm, organic',
    'dusty-blush': 'Soft, romantic',
    blush: 'Soft, romantic',
    blue: 'Bold, modern',
    'brushed-grey': 'Industrial, neutral',
    grey: 'Industrial, neutral',
    'brushed-silver': 'Cool, modern',
    silver: 'Cool, modern',
    gold: 'Warm, ornate',
    brass: 'Warm, ornate',
  };

  function describeFrameColor(fc) {
    if (!fc) return '';
    if (fc.description) return fc.description;
    // Try the handle first, then name-derived key (e.g. "Sage Green" → "sage-green")
    const byHandle = FRAME_COLOR_DESCRIPTORS[(fc.handle || '').toLowerCase()];
    if (byHandle) return byHandle;
    const nameKey = (fc.name || '').toLowerCase().replace(/\s+/g, '-');
    return FRAME_COLOR_DESCRIPTORS[nameKey] || '';
  }

  function selectFrameColor(handle) {
    const fc = frameColorMap[handle];
    if (!fc) return;
    state.frameColorHandle = handle;
    document.dispatchEvent(new CustomEvent('rmf:color-changed', { detail: { handle, colorName: fc.label || handle, productId: data.productId } }));
    if (fc.mouldingTile) flipTileAsync(fc.mouldingTile);

    // Update form property
    if (propFrameColor) propFrameColor.value = handle;

    // Update row header summary + swatch icon
    if (colorSummaryEl) colorSummaryEl.textContent = fc.name;

    // Update H1 title sub: "— Black Frame" → "— Grey Frame"
    // The data-suffix is whatever followed the colour name in the original
    // title (e.g. "Frame"); empty when the suffix was just the colour itself
    // ("The Grain Frame - Oak" → suffix=""). Build the string conditionally
    // so we don't leave a trailing space when suffix is empty.
    const titleSubEl = document.querySelector('.rmf-title__sub');
    if (titleSubEl) {
      const suffix = (titleSubEl.dataset.suffix || '').trim();
      titleSubEl.textContent = suffix ? `— ${fc.name} ${suffix}` : `— ${fc.name}`;
    }
    const stickyTitleEl = document.querySelector('.pdp-sticky-cta__title');
    if (stickyTitleEl) stickyTitleEl.textContent = document.querySelector('.rmf-title')?.textContent.trim() || '';

    // Update hint name + descriptor below the swatch grid (e.g. "Sage Green · Muted, contemporary")
    const colorHintNameEl = document.querySelector('#rmf-color-hint .rmf-color-hint__name');
    const colorHintSubEl = document.getElementById('rmf-color-hint-sub');
    if (colorHintNameEl) colorHintNameEl.textContent = fc.name;
    if (colorHintSubEl) colorHintSubEl.textContent = describeFrameColor(fc);
    if (colorIconSwatch) {
      if (fc.mouldingTile) {
        colorIconSwatch.style.backgroundImage = `url('${fc.mouldingTile}')`;
        colorIconSwatch.style.backgroundSize = '125%';
        colorIconSwatch.style.backgroundPosition = 'center';
        colorIconSwatch.style.backgroundColor = '';
      } else {
        colorIconSwatch.style.backgroundImage = '';
        colorIconSwatch.style.backgroundColor = fc.hex;
      }
    }

    // Swap hero raster if this frame color has a pre-baked mock image,
    // otherwise reveal the live canvas mock so the colour change is visible.
    if (heroImg && fc.mockImageUrl) {
      heroImg.src = fc.mockImageUrl;
      heroImg.srcset = '';
      heroImg.style.removeProperty('visibility');
      if (heroCss3d && fc.mockCorners) {
        heroCss3d.dataset.corners = JSON.stringify(fc.mockCorners);
        heroCss3d.dataset.frameAspect = String(fc.mockFrameAspect || 1.0);
      }
      if (frameMock) {
        frameMock.classList.add('rmf-frame-mock--behind-hero');
      }
    } else if (frameMock) {
      // Fall back to canvas mock when no pre-baked raster for this color.
      // Make the canvas visible (it's hidden by default behind heroImg) and
      // hide heroImg so the user actually sees the colour change.
      frameMock.classList.remove('rmf-frame-mock--behind-hero');
      if (heroImg) heroImg.style.visibility = 'hidden';
      frameMock.updateConfig?.({
        frameColor: fc.hex,
        mouldingTile: fc.mouldingTile || '',
        mouldingWidth: fc.mouldingWidth || data.mouldingWidth,
      });
    }

    // Update active state on swatch buttons
    frameColorOptionsEl?.querySelectorAll('.rmf-frame-color-btn').forEach(btn => {
      const active = btn.dataset.colorHandle === handle;
      btn.classList.toggle('rmf-frame-color-btn--active', active);
      btn.setAttribute('aria-selected', String(active));
    });

    updateSpecsCard();

    // Refresh the gallery thumb strip for the newly-selected colour.
    // Tear down all non-mock thumbs and rebuild from whichever AI shots are
    // populated on the colour's frame_color metaobject (closeup, lifestyle
    // room, gallery wall, leaning, corner detail). Always-rebuild — even
    // when the new colour has zero AI shots — keeps the strip honest:
    // stale thumbs from the previous colour never linger, and a colour
    // with no shots simply renders the mock thumb on its own. If the user
    // was viewing a non-mock thumb (lifestyle hero overlay), promote the
    // first new shot into the hero so the gallery follows the colour
    // change rather than going blank.
    const thumbsContainer = document.getElementById('rmf-thumbs');
    if (thumbsContainer) {
      const wasOnNonMockThumb = thumbsContainer.querySelector('.rmf-thumb--active:not(.rmf-thumb--mock):not(.rmf-thumb--schematic)') !== null;
      // Preserve the mock thumb and the moulding schematic thumb — the
      // schematic is profile-level (shared across every colour), so it must
      // survive colour changes rather than being torn down and never re-added.
      thumbsContainer.querySelectorAll('.rmf-thumb:not(.rmf-thumb--mock):not(.rmf-thumb--schematic)').forEach(t => t.remove());

      const aiShots = [
        { data: fc.aiLifestyleRoom,  kind: 'lifestyleRoom' },
        { data: fc.aiGalleryWall,    kind: 'galleryWall' },
        { data: fc.aiLeaning,        kind: 'leaning' },
        { data: fc.aiCornerDetail,   kind: 'cornerDetail' },
      ].filter(s => s.data && s.data.full);

      aiShots.forEach(({ data, kind }) => {
        const btn = document.createElement('button');
        btn.className = 'rmf-thumb';
        btn.setAttribute('role', 'listitem');
        btn.dataset.thumbType = 'image';
        btn.dataset.thumbKind = kind;
        btn.dataset.src = data.full;
        btn.setAttribute('aria-label', `${fc.name} — ${kind}`);
        const imgEl = document.createElement('img');
        imgEl.src = data.thumb || data.full;
        imgEl.alt = '';
        imgEl.width = 120;
        imgEl.height = 120;
        imgEl.loading = 'lazy';
        btn.appendChild(imgEl);
        btn.addEventListener('click', () => {
          deactivateAllThumbs();
          btn.classList.add('rmf-thumb--active');
          if (lifestyleImg) {
            lifestyleImg.src = data.full;
            lifestyleImg.alt = `${fc.name} — ${kind}`;
          }
          if (lifestyleOverlay) {
            lifestyleOverlay.classList.remove('rmf-lifestyle-overlay--schematic');
            lifestyleOverlay.setAttribute('aria-hidden', 'false');
          }
        });
        thumbsContainer.appendChild(btn);
      });

      // Keep the profile-level schematic thumb pinned to the end of the strip
      // after the colour shots have been re-appended above.
      const schematicThumb = thumbsContainer.querySelector('.rmf-thumb--schematic');
      if (schematicThumb) thumbsContainer.appendChild(schematicThumb);

      // If the user was viewing a lifestyle thumb before changing colour,
      // promote the first new shot into the hero so the gallery follows.
      if (wasOnNonMockThumb && aiShots.length > 0) {
        const firstThumb = thumbsContainer.querySelector('.rmf-thumb:not(.rmf-thumb--mock):not(.rmf-thumb--schematic)');
        firstThumb?.click();
      }
    }

    refreshRoomPreview();
  }

  renderFrameColorOptions();

  // Seed initial color descriptor below the frame-color tile row.
  {
    const initialFc = frameColorMap[state.frameColorHandle];
    const colorHintSubEl = document.getElementById('rmf-color-hint-sub');
    if (colorHintSubEl && initialFc) colorHintSubEl.textContent = describeFrameColor(initialFc);
  }

  // ── Measure lines overlay ────────────────────────────────────────────────

  const MOUNT_WIDTH_MM = {
    'no-mount':         0,
    'single-mount':     40,
    'double-mount':     40,
    'deep-mount':       60,
    'double-deep-mount':60,
    'mirror':           0,
  };

  const measureOverlaySvg = document.getElementById('rmf-measure-overlay');
  const measureBtn        = document.getElementById('rmf-measure-btn');
  let measuresVisible     = false;

  function getMeasureData() {
    const mouldingMm = getFrameProp('mouldingWidth') || data.mouldingWidth || 15;
    const mountMm    = MOUNT_WIDTH_MM[state.borderOption] || 0;

    const activeBtn = isTierMode
      ? document.querySelector(`.rmf-size-btn[data-size-handle="${state.sizeHandle}"]`)
      : document.querySelector('.rmf-size-btn--active');

    // The size-option metaobject's numeric fields (`width_inches`,
    // `width_cms`, …) are inconsistently populated across tiers — some
    // store the paper size, others store the visible aperture (paper minus
    // rebate). The size handle ("18x24") is the only reliable source for
    // the paper dimensions, so parse that first. Fall back to the metaobject
    // numerics only when the handle isn't a numeric WxH pattern.
    function sizeFromActive() {
      const oriented = (w, h) => orientDims(w, h);
      if (!activeBtn) {
        return oriented(data.sizeOptions?.[0]?.widthIn || 16, data.sizeOptions?.[0]?.heightIn || 20);
      }
      const parts = String(activeBtn.dataset.sizeHandle || '').split(/[x×-]/).map(Number).filter(n => n > 0);
      if (parts.length >= 2) return oriented(parts[0], parts[1]);
      const wCm = parseFloat(activeBtn.dataset.widthCm);
      const hCm = parseFloat(activeBtn.dataset.heightCm);
      if (wCm > 0 && hCm > 0) return oriented(wCm / 2.54, hCm / 2.54);
      const wIn = parseFloat(activeBtn.dataset.widthIn);
      const hIn = parseFloat(activeBtn.dataset.heightIn);
      if (wIn > 0 && hIn > 0) return oriented(wIn, hIn);
      return oriented(16, 20);
    }
    const sz = sizeFromActive();
    let paperWIn = sz.w;
    let paperHIn = sz.h;

    // Physical model (matches gallery-wall-frame-mock & 3D bundle):
    //   overall = paper + 2 × (moulding − rebate)
    //   visible = paper − 2 × rebate − 2 × mount
    // `rebateMm` is hardcoded in both renderers so mirror it here.
    const REBATE_MM = 12.7;
    const paperWMm  = paperWIn * 25.4;
    const paperHMm  = paperHIn * 25.4;
    const overhang  = Math.max(0, mouldingMm - REBATE_MM);
    const overallWMm = paperWMm + 2 * overhang;
    const overallHMm = paperHMm + 2 * overhang;
    const visibleWMm = Math.max(0, paperWMm - 2 * REBATE_MM - 2 * mountMm);
    const visibleHMm = Math.max(0, paperHMm - 2 * REBATE_MM - 2 * mountMm);

    // Format a small mm measurement (moulding / mount profile) into the
    // user's preferred unit. Small values → 1 decimal place to stay readable.
    function smallLabel(mm) {
      if (displayUnit === 'cm') return `${(mm / 10).toFixed(1)}cm`;
      if (displayUnit === 'in') return `${(mm / 25.4).toFixed(1)}"`;
      return `${Math.round(mm)}mm`;
    }

    // Always render to 1 decimal so the label honestly reflects the
    // underlying precision — "20"" reads as exactly 20.0" but the actual
    // dimension is typically 19.9 or 20.1 (paper + moulding − rebate
    // rarely lands on a whole number). Keeping the ".0" tells the user
    // the value is rounded, not exact, which matters when deciding
    // whether the frame fits a specific space.
    const cleanNum = (n) => (Math.round(n * 10) / 10).toFixed(1);

    const toUnit = (mm) => displayUnit === 'cm' ? mm / 10 : mm / 25.4;
    const unitSuffix = displayUnit === 'cm' ? 'cm' : '"';

    const visibleWLabel = `${cleanNum(toUnit(visibleWMm))}${unitSuffix}`;
    const visibleHLabel = `${cleanNum(toUnit(visibleHMm))}${unitSuffix}`;
    const overallWLabel = `${cleanNum(toUnit(overallWMm))}${unitSuffix}`;
    const overallHLabel = `${cleanNum(toUnit(overallHMm))}${unitSuffix}`;

    const mouldingLabel = smallLabel(mouldingMm);
    const mountLabel    = mountMm > 0 ? smallLabel(mountMm) : '';

    return {
      mouldingMm, mountMm, mouldingLabel, mountLabel,
      // Inner bracket labels refer to the VISIBLE area (inside the rebate
      // and any mount), not the paper itself. Kept named `paper*Label` for
      // backwards compatibility with the 3D bundle's existing API.
      paperWLabel: visibleWLabel,
      paperHLabel: visibleHLabel,
      overallWLabel, overallHLabel,
    };
  }

  function ns(tag) {
    return document.createElementNS('http://www.w3.org/2000/svg', tag);
  }

  function svgEl(tag, attrs) {
    const e = ns(tag);
    Object.entries(attrs).forEach(([k, v]) => e.setAttribute(k, String(v)));
    return e;
  }

  function renderMeasureOverlay() {
    if (!measureOverlaySvg || !frameMock) return;
    const layout = frameMock.getRenderLayout?.();
    if (!layout) return;

    const { bufferSize: S, frameRect: fr, frameWidthPx: fw, matWidthPx: mw } = layout;
    const d = getMeasureData();

    // ── SVG user-unit scale ───────────────────────────────────────────────
    // font-size as SVG presentation attribute (user units) renders correctly
    // relative to the viewBox, unlike px in CSS which is absolute.
    const cssW  = frameMock.getBoundingClientRect().width || (S / 2);
    const u     = S / cssW;   // SVG user units per CSS pixel
    const fsMaj = Math.round(13 * u);   // overall dims — 13px CSS equivalent
    const fsMin = Math.round(11 * u);   // paper dims
    const fsTin = Math.round(10 * u);   // component dims (moulding/mount)
    const swDim = Math.max(1.5, 1.8 * u);
    const swExt = Math.max(1,   1.2 * u);

    measureOverlaySvg.innerHTML = '';
    measureOverlaySvg.setAttribute('viewBox', `0 0 ${S} ${S}`);

    // Arrowhead: orient=auto-start-reverse + refX=0 → tip extends OUTWARD at both ends
    const defs = ns('defs');
    const asz  = Math.max(6, 8 * u);
    const ahalf = asz / 2;
    const mk = svgEl('marker', {
      id: 'rmf-ao', markerWidth: asz, markerHeight: asz,
      refX: 0, refY: ahalf,
      orient: 'auto-start-reverse',
      markerUnits: 'userSpaceOnUse',
    });
    mk.appendChild(svgEl('path', { d: `M0,0 L${asz},${ahalf} L0,${asz}`, fill: '#111' }));
    defs.appendChild(mk);
    measureOverlaySvg.appendChild(defs);

    const MA = 'url(#rmf-ao)';
    const outlineW = (swDim * 3.5).toFixed(1);

    // Helpers ─────────────────────────────────────────────────────────────
    function L(x1, y1, x2, y2, isDim, ms, me) {
      const l = svgEl('line', {
        x1, y1, x2, y2,
        stroke: isDim ? '#111' : 'rgba(0,0,0,0.3)',
        'stroke-width': isDim ? swDim : swExt,
        fill: 'none',
        ...(isDim ? {} : { 'stroke-dasharray': `${3*u} ${2.5*u}` }),
      });
      if (ms) l.setAttribute('marker-start', ms);
      if (me) l.setAttribute('marker-end',   me);
      measureOverlaySvg.appendChild(l);
    }

    // Text with white outline behind black fill for readability on any bg
    function T(x, y, str, anchor, fontSize, rot) {
      // Outline pass
      const bg = svgEl('text', {
        x, y, 'text-anchor': anchor, 'dominant-baseline': 'middle',
        'font-size': fontSize, 'font-weight': '700', 'letter-spacing': '-0.01em',
        fill: 'none', stroke: 'rgba(255,255,255,0.92)',
        'stroke-width': outlineW, 'stroke-linejoin': 'round', 'paint-order': 'stroke',
      });
      if (rot) bg.setAttribute('transform', `rotate(${rot} ${x} ${y})`);
      bg.textContent = str;
      measureOverlaySvg.appendChild(bg);
      // Fill pass
      const fg = svgEl('text', {
        x, y, 'text-anchor': anchor, 'dominant-baseline': 'middle',
        'font-size': fontSize, 'font-weight': '700', 'letter-spacing': '-0.01em',
        fill: '#111',
      });
      if (rot) fg.setAttribute('transform', `rotate(${rot} ${x} ${y})`);
      fg.textContent = str;
      measureOverlaySvg.appendChild(fg);
    }

    // Aperture rect — the visible artwork area (paper minus rebate minus
    // any mount). This is what the inner bracket measures.
    const apt = {
      x: fr.x + fw + mw,
      y: fr.y + fw + mw,
      w: fr.w - 2*(fw + mw),
      h: fr.h - 2*(fw + mw),
    };

    // Available margins (canvas edge to frame edge)
    const topM = fr.y;                  // space above frame
    const rgtM = S - (fr.x + fr.w);    // space right of frame
    const lftM = fr.x;                  // space left of frame
    const ov   = topM * 0.07;           // extension line overshoot

    // ── TOP: nested width brackets ────────────────────────────────────────
    // Outer bracket (overall frame width) sits furthest from frame.
    // Inner bracket (paper width) sits between outer and frame edge.
    // Extension lines start at fr.y; the inner ones are set at apt.x to
    // show the paper boundary position.
    const topOuter = topM * 0.28;
    const topInner = topM * 0.66;

    // Outer: overall width — fr.x → fr.x+fr.w
    L(fr.x,           fr.y, fr.x,           topOuter - ov, false);
    L(fr.x + fr.w,    fr.y, fr.x + fr.w,    topOuter - ov, false);
    L(fr.x, topOuter, fr.x + fr.w, topOuter, true, MA, MA);
    T(fr.x + fr.w/2, topOuter - fsMaj * 0.75, d.overallWLabel, 'middle', fsMaj);

    // Inner: visible width — span matches the aperture (inside rebate + mount)
    L(apt.x,           fr.y, apt.x,          topInner - ov, false);
    L(apt.x + apt.w,   fr.y, apt.x + apt.w,  topInner - ov, false);
    L(apt.x, topInner, apt.x + apt.w, topInner, true, MA, MA);
    T(apt.x + apt.w/2, (topOuter + topInner)/2, d.paperWLabel, 'middle', fsMin);

    // ── RIGHT: nested height brackets ─────────────────────────────────────
    // Outer: overall height. Inner: paper height.
    const rgtOuter = fr.x + fr.w + rgtM * 0.60;
    const rgtInner = fr.x + fr.w + rgtM * 0.27;

    // Outer: overall height — fr.y → fr.y+fr.h
    L(fr.x + fr.w, fr.y,        rgtOuter + ov, fr.y,        false);
    L(fr.x + fr.w, fr.y + fr.h, rgtOuter + ov, fr.y + fr.h, false);
    L(rgtOuter, fr.y, rgtOuter, fr.y + fr.h, true, MA, MA);
    T(rgtOuter + fsMaj * 0.75, fr.y + fr.h/2, d.overallHLabel, 'middle', fsMaj, -90);

    // Inner: visible height — span matches the aperture
    L(fr.x + fr.w, apt.y,           rgtInner + ov, apt.y,           false);
    L(fr.x + fr.w, apt.y + apt.h,   rgtInner + ov, apt.y + apt.h,   false);
    L(rgtInner, apt.y, rgtInner, apt.y + apt.h, true, MA, MA);
    T((rgtOuter + rgtInner)/2, apt.y + apt.h/2, d.paperHLabel, 'middle', fsMin, -90);

    // Moulding / mount width brackets intentionally omitted — per UX feedback
    // these cross-section measurements aren't useful to customers.
  }

  // ── 3D measure sync ──────────────────────────────────────────────────────
  // The Three.js bundle handles its own dimension-line rendering — we just
  // push the current frame config (moulding, mount, labels) whenever it
  // changes. Only visually active in orbit mode (no room photo).

  function syncMeasurements3d() {
    const instance = roomPreviewState.instance;
    if (!instance?.showMeasurements) return;
    const inOrbit = mockWrap?.classList.contains('rmf-mock-wrap--room-mode') && !roomPreviewState.showingRoom;
    if (measuresVisible && inOrbit) {
      const d = getMeasureData();
      instance.showMeasurements(true, {
        mouldingMm:     d.mouldingMm,
        mountMm:        d.mountMm,
        mouldingLabel:  d.mouldingLabel,
        mountLabel:     d.mountLabel,
        overallWLabel:  d.overallWLabel,
        overallHLabel:  d.overallHLabel,
        paperWLabel:    d.paperWLabel,
        paperHLabel:    d.paperHLabel,
      });
    } else {
      instance.showMeasurements(false);
    }
  }

  // ── Measure toggle button (2D + 3D) ──────────────────────────────────────

  if (measureBtn) {
    measureBtn.addEventListener('click', () => {
      measuresVisible = !measuresVisible;
      measureBtn.setAttribute('aria-pressed', String(measuresVisible));
      mockWrap?.classList.toggle('rmf-mock-wrap--measures', measuresVisible);
      const inOrbit = mockWrap?.classList.contains('rmf-mock-wrap--room-mode') && !roomPreviewState.showingRoom;
      if (inOrbit) {
        syncMeasurements3d();
      } else if (measuresVisible) {
        renderMeasureOverlay();
      }
    });
  }

  if (frameMock) {
    frameMock.addEventListener('rmf:rendered', () => {
      if (measuresVisible && !mockWrap?.classList.contains('rmf-mock-wrap--room-mode')) {
        renderMeasureOverlay();
      }
    });
  }

  // ── Thumbnail strip ──────────────────────────────────────────────────────

  const thumbsContainerEl = document.getElementById('rmf-thumbs');
  function deactivateAllThumbs() {
    // Use a live query so dynamically-added colour thumbnails are included.
    (thumbsContainerEl || section)
      .querySelectorAll('.rmf-thumb')
      .forEach(t => t.classList.remove('rmf-thumb--active'));
  }

  thumbs.forEach(thumb => {
    thumb.addEventListener('click', () => {
      deactivateAllThumbs();
      thumb.classList.add('rmf-thumb--active');

      const type = thumb.dataset.thumbType;

      // Thumbs only toggle the lifestyle/corner-detail overlay on top of the
      // hero. The Frame ↔ Your wall mode toggle (the pill) owns
      // `--room-mode` and the underlying 3D vs canvas state — touching it
      // here would race that toggle and stomp the 3D scene the user set up.
      if (type === 'image') {
        const src = thumb.dataset.src;
        if (lifestyleImg && src) {
          lifestyleImg.src = src;
          lifestyleImg.alt = thumb.querySelector('img')?.alt || '';
        }
        if (lifestyleOverlay) {
          // The schematic is line-art: show it whole on a clean ground
          // (object-fit: contain) instead of cover-cropping its callouts.
          lifestyleOverlay.classList.toggle('rmf-lifestyle-overlay--schematic', thumb.dataset.thumbKind === 'schematic');
          lifestyleOverlay.setAttribute('aria-hidden', 'false');
        }
      } else {
        // Frame-preview thumb (mock) — just dismiss the overlay.
        if (lifestyleOverlay) lifestyleOverlay.setAttribute('aria-hidden', 'true');
      }
    });
  });

  // ── 3D room preview (lazy-loaded) ─────────────────────────────────────────
  //
  // First activation: user picks a wall photo → POST /api/room-analysis to
  // run MapAnything on the server → fetch /api/frame-preview-config for the
  // frame's baked profile points → inject the room-3d-preview-bundle.js
  // script (Three.js + RoomViewer live in that bundle, not here) →
  // window.Room3DPreview.mount() renders the frame on the detected wall.

  // Shop-chat-agent routes API + static bundles under the `/gallery-wall`
  // path (mirrors the Shopify app proxy subpath config in
  // shopify.app.gallery-wall.toml). BACKEND_HOST is the full base including
  // that prefix so we can concat `/api/...` and `/room-3d-preview-bundle.js`
  // cleanly. Same pattern as extensions/chat-bubble/assets/room-fit-block.js.
  //
  // Local dev: set the "Backend host override (dev)" theme setting to your
  // current `shopify app dev` tunnel URL (+ `/gallery-wall`). The section
  // Liquid renders that into `window.RMF_SHOP_CHAT_AGENT_HOST` before this
  // script runs.
  const BACKEND_HOST = window.RMF_SHOP_CHAT_AGENT_HOST
    || 'https://shop-chat-agent.iamfy.tech/gallery-wall';
  const SESSION_STORAGE_KEY = `rmf:room:${data.handle}`;

  // Cross-PDP wall-analysis cache. Stores ONLY the analyseRoom() response
  // (envmap URLs + wall planes + photoHash) — every PDP rebuilds its own
  // per-product mount payload from this + the current frame config. Keyed
  // by origin so it survives reloads and works on any PDP type (readymade,
  // art-print, gallery wall). 30-day TTL — server-side assets behind the
  // URLs may rotate.
  const WALL_ANALYSIS_STORAGE_KEY = 'fy:wall-analysis';
  const WALL_ANALYSIS_TTL_MS = 30 * 24 * 60 * 60 * 1000; // 30 days

  function loadPersistedWallAnalysis() {
    try {
      const raw = localStorage.getItem(WALL_ANALYSIS_STORAGE_KEY);
      if (!raw) return null;
      const parsed = JSON.parse(raw);
      if (!parsed || !parsed.analysis || !parsed.savedAt) return null;
      if (Date.now() - parsed.savedAt > WALL_ANALYSIS_TTL_MS) {
        localStorage.removeItem(WALL_ANALYSIS_STORAGE_KEY);
        return null;
      }
      return parsed.analysis;
    } catch { return null; }
  }

  function persistWallAnalysis(analysis) {
    if (!analysis) return;
    try {
      localStorage.setItem(WALL_ANALYSIS_STORAGE_KEY, JSON.stringify({
        analysis,
        savedAt: Date.now(),
      }));
    } catch { /* storage full / private mode — non-fatal */ }
  }

  function clearPersistedWallAnalysis() {
    try { localStorage.removeItem(WALL_ANALYSIS_STORAGE_KEY); } catch { /* noop */ }
  }

  function bundleSrc() {
    return `${BACKEND_HOST}/room-3d-preview-bundle.js`;
  }

  // Cache-bust the bundle with the app's build tag so deploys show up
  // immediately instead of after the 4h browser/Cloudflare cache TTL (same
  // pattern as whisper-block.js / atc-sheet.js). The tiny version probe is
  // fetched no-store so it can't go stale itself.
  async function versionedBundleSrc() {
    let v = Date.now();
    try {
      const origin = new URL(BACKEND_HOST).origin;
      const res = await fetch(`${origin}/gallery-wall-version.json`, { cache: 'no-store' });
      if (res.ok) {
        const j = await res.json();
        if (j && j.version) v = j.version;
      }
    } catch { /* fall back to timestamp bust */ }
    return `${bundleSrc()}?v=${v}`;
  }

  // The room-3d-preview-bundle treats Three.js as an external (window.THREE),
  // so we must load Three from a CDN before injecting the bundle. The bundle
  // extends THREE.Controls (added in r170) but Three dropped the UMD build
  // after r160 — so we import Three as an ES module and pin it to window.THREE.
  // Override by setting `window.RMF_THREE_SRC` to point at a different module.
  const THREE_MODULE_SRC = window.RMF_THREE_SRC
    || 'https://cdn.jsdelivr.net/npm/three@0.170.0/build/three.module.min.js';

  function setPreviewStatus(kind, msg = '') {
    if (!roomPreviewEl) return;
    roomPreviewEl.classList.remove('rmf-room-preview--loading', 'rmf-room-preview--error');
    if (kind === 'loading') roomPreviewEl.classList.add('rmf-room-preview--loading');
    if (kind === 'error')   roomPreviewEl.classList.add('rmf-room-preview--error');
    if (roomPreviewStatus) roomPreviewStatus.textContent = msg;
  }

  function loadScriptOnce(src, ready) {
    if (ready()) return Promise.resolve();
    // De-dupe across callers and across other features that may have already
    // injected the same script tag (e.g. another section using Three).
    const existing = document.querySelector(`script[src="${src}"]`);
    if (existing) {
      if (existing.dataset.loaded === 'true') return Promise.resolve();
      return new Promise((resolve, reject) => {
        existing.addEventListener('load', () => ready() ? resolve() : reject(new Error(`Script loaded but ready check failed: ${src}`)));
        existing.addEventListener('error', () => reject(new Error(`Failed to load ${src}`)));
      });
    }
    return new Promise((resolve, reject) => {
      const s = document.createElement('script');
      s.src = src;
      s.async = true;
      // Plain UMD scripts only — no crossorigin/integrity to keep this generic.
      s.onload = () => {
        s.dataset.loaded = 'true';
        ready() ? resolve() : reject(new Error(`Script loaded but ready check failed: ${src}`));
      };
      s.onerror = () => reject(new Error(`Failed to load ${src}`));
      document.head.appendChild(s);
    });
  }

  function loadThreeModule() {
    if (window.THREE) return Promise.resolve();
    // Dynamic ESM import — the only way to get r170+ since Three dropped UMD.
    // The result is the namespace object containing every THREE export, so
    // pinning it to window.THREE makes the bundle's `window.THREE.Foo`
    // references resolve. Keep the import URL as a string literal so bundlers
    // / minifiers don't rewrite it.
    return import(/* @vite-ignore */ THREE_MODULE_SRC).then(mod => {
      window.THREE = mod;
    });
  }

  function loadPreviewBundle() {
    if (window.Room3DPreview?.mount) return Promise.resolve();
    if (roomPreviewState.bundleReady) return roomPreviewState.bundleReady;
    // Three first — the bundle references the THREE global synchronously at
    // top level, so it MUST exist before the bundle script parses. Sequential.
    roomPreviewState.bundleReady = loadThreeModule()
      .then(() => versionedBundleSrc())
      .then((src) => loadScriptOnce(src, () => !!window.Room3DPreview?.mount));
    return roomPreviewState.bundleReady;
  }



  function readFileAsDataUrl(file) {
    return new Promise((resolve, reject) => {
      const r = new FileReader();
      r.onload = () => resolve(r.result);
      r.onerror = () => reject(r.error || new Error('file read failed'));
      r.readAsDataURL(file);
    });
  }

  /**
   * Kick off the room-analysis pipeline and wait for the result. The
   * server responds immediately with either `status: 'ready'` (cache hit)
   * or `status: 'processing'` (background run started) — if processing,
   * we poll until the Room row shows up, so the fetch never blocks for
   * the full ~30-60s fresh-analysis duration (which would time out at
   * Cloudflare's ~100s tunnel limit).
   *
   * `onProgress(elapsedSeconds)` fires each poll so the UI can update
   * its "Analysing your wall (Xs)..." status line.
   */
  // fetch with a per-request timeout, optionally tied to an outer abort
  // signal. Without this a stalled request hangs its await forever — the
  // poll loops only check their 3-minute wall between iterations, so a
  // single hung fetch left the UI on "Analysing wall…" with no way out.
  async function fetchWithTimeout(url, opts, outerSignal, timeoutMs) {
    const ctrl = new AbortController();
    const onAbort = () => ctrl.abort();
    outerSignal?.addEventListener('abort', onAbort, { once: true });
    const timer = setTimeout(onAbort, timeoutMs);
    try {
      return await fetch(url, { ...opts, signal: ctrl.signal });
    } finally {
      clearTimeout(timer);
      outerSignal?.removeEventListener('abort', onAbort);
    }
  }
  const abortError = () => new DOMException('Flow superseded', 'AbortError');

  async function analyseRoom(dataUrl, onProgress, signal) {
    // Generous POST timeout — the body carries the room photo data URL,
    // which can take a while to upload on a slow mobile uplink.
    const postRes = await fetchWithTimeout(`${BACKEND_HOST}/api/room-analysis`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ imageDataUrl: dataUrl }),
    }, signal, 120000);
    if (!postRes.ok && postRes.status !== 202) {
      const txt = await postRes.text().catch(() => '');
      throw new Error(`room-analysis: ${postRes.status} ${txt.slice(0, 200)}`);
    }
    const initial = await postRes.json();
    if (initial.status === 'ready') return initial;
    if (initial.status !== 'processing') throw new Error(`room-analysis: unexpected status ${initial.status}`);

    const { photoHash } = initial;
    if (!photoHash) throw new Error('room-analysis: missing photoHash');

    const startMs = Date.now();
    const POLL_INTERVAL_MS = 3000;
    const TIMEOUT_MS = 3 * 60 * 1000; // 3 minutes

    while (true) {
      const elapsed = (Date.now() - startMs) / 1000;
      onProgress?.(elapsed);
      if (Date.now() - startMs > TIMEOUT_MS) {
        throw new Error('room-analysis: timed out after 3 minutes');
      }
      await new Promise(r => setTimeout(r, POLL_INTERVAL_MS));
      if (signal?.aborted) throw abortError();
      let body;
      try {
        const pollRes = await fetchWithTimeout(
          `${BACKEND_HOST}/api/room-analysis?photoHash=${encodeURIComponent(photoHash)}`,
          {}, signal, 15000
        );
        if (!pollRes.ok) continue; // transient failure — try again next tick
        body = await pollRes.json();
      } catch (e) {
        if (signal?.aborted) throw e;
        continue; // transient network/timeout/non-JSON body — try again next tick
      }
      if (body.status === 'ready') return body;
    }
  }

  function pickSizeForPreview() {
    // Prefer the size handle ("18x24" → 18×24 paper inches) as the
    // authoritative source. The metaobject numerics are inconsistently
    // populated across tiers so the handle is the only reliable paper dim.
    // Dims are returned in the active orientation — the frame is rotatable.
    const active = document.querySelector('.rmf-size-btn.rmf-size-btn--active') || sizeBtns[0];
    const handle = String(active?.dataset.sizeHandle || state.sizeHandle || '');
    const parts = handle.split(/[x×-]/).map(Number).filter(n => n > 0);
    if (parts.length >= 2) {
      const d = orientDims(parts[0], parts[1]);
      return { width_inches: d.w, height_inches: d.h };
    }
    const w = parseFloat(active?.dataset.widthIn);
    const h = parseFloat(active?.dataset.heightIn);
    if (w && h) {
      const d = orientDims(w, h);
      return { width_inches: d.w, height_inches: d.h };
    }
    const d = orientDims(16, 20);
    return { width_inches: d.w, height_inches: d.h };
  }

  function blitRoomThumb() {
    if (!thumbRoomCanvas || !roomPreviewStage) return;
    // Double-rAF: skip past any in-flight Three.js render scheduled this frame
    requestAnimationFrame(() => requestAnimationFrame(() => {
      const threeCanvas = roomPreviewStage.querySelector('canvas');
      if (!threeCanvas) return;
      const tctx = thumbRoomCanvas.getContext('2d');
      tctx.clearRect(0, 0, thumbRoomCanvas.width, thumbRoomCanvas.height);
      tctx.drawImage(threeCanvas, 0, 0, thumbRoomCanvas.width, thumbRoomCanvas.height);
      thumbRoomCanvas.parentElement?.classList.add('rmf-thumb--room-rendered');
    }));
  }

  // Spotlight wash overlay — matches the 2D mock's WALL_LIGHT_DEFAULT params:
  // lightPos [0.40, 0.22], spotAlpha 0.04. Injected once into roomPreviewStage.
  let _lightWashEl = null;
  function ensureLightWash() {
    if (_lightWashEl || !roomPreviewStage) return;
    _lightWashEl = document.createElement('div');
    _lightWashEl.style.cssText = [
      'position:absolute', 'inset:0', 'pointer-events:none', 'z-index:1',
      'background:radial-gradient(ellipse at 40% 22%,rgba(255,255,255,0.04) 0%,rgba(255,255,255,0.022) 40%,transparent 75%)',
    ].join(';');
    roomPreviewStage.appendChild(_lightWashEl);
  }

  function applyRoomWallColor(hex) {
    if (!hex || !roomPreviewStage) return;
    roomPreviewState.instance?.setWallColor?.(hex);
  }

  async function mountRoomPreview(payload) {
    if (roomPreviewState.instance) {
      try { roomPreviewState.instance.dispose?.(); } catch { /* noop */ }
      roomPreviewState.instance = null;
    }
    await loadPreviewBundle();
    roomPreviewState.instance = await window.Room3DPreview.mount(roomPreviewStage, payload);
    // Order matters here. applyRoomWallColor → setWallColor → sets the
    // backdrop material's *color* to the cream tint. PreviewEnhance then
    // builds a pre-tinted plaster texture and resets color to (1,1,1) so
    // the texture isn't multiplied a second time. Reverse the order and
    // the texture comes out darker (texture × cream tint).
    if (payload._default) {
      applyRoomWallColor(payload.wallColor || '#f5f0e8');
      ensureLightWash();
    }
    // Compositing strategy on the live PDP:
    //   - The static hero raster (Gemini bake) stays visible underneath
    //     the canvas. It paints the wall (cream, at the bake's 25° yaw).
    //   - The 3D canvas renders with clearAlpha=0 + backdrop hidden, so
    //     the hero raster shows through everywhere except where the
    //     frame meshes + shadow/AO quads paint.
    //   - PreviewEnhance.applyToInstance applies the same camera yaw
    //     (25°) the bake used so the rendered frame meshes line up with
    //     the static raster's frame position.
    //   - Shadow + AO quads stay visible — they alpha-blend onto the
    //     hero raster's wall, giving the frame proper grounding.
    if (payload._default) {
      try {
        const v = roomPreviewState.instance?.viewer;
        if (v?.renderer) v.renderer.setClearColor(0x000000, 0);
        if (v?.backdrop) v.backdrop.visible = false;
        // Apply camera yaw + material tweaks to match the bake pose.
        // skipBackdrop avoids painting a plaster texture we'd never see.
        window.PreviewEnhance?.applyToInstance?.(roomPreviewState.instance, {
          wallColor: payload.wallColor || '#f5f0e8',
          skipBackdrop: true,
        });
      } catch (e) { console.warn('[readymade-pdp] 3d setup failed:', e); }
    }
    roomPreviewState.lastPayload = payload;
    const hasRealPhoto = !payload._default;
    roomPreviewState.showingRoom = hasRealPhoto;
    roomPreviewEl.classList.toggle('rmf-room-preview--has-photo', hasRealPhoto);
    // Reflect the wall-photo presence on the hero mode pill so the change-photo
    // icon-button + divider reveal and the wall tab label switches from the
    // "See it on your wall" upload affordance to a plain "Your wall" tab.
    const heroPill = document.querySelector('.rmf-hero-mode-tabs');
    if (heroPill) heroPill.classList.toggle('pdp-mode-tabs--has-wall', !!roomPreviewState.analysis);
    syncWallTabLabel();
    // Push current measure state to the bundle (orbit mode only shows them).
    syncMeasurements3d();
    setPreviewStatus(null);
    blitRoomThumb();
    // First successful wall-photo mount — surface the drag-to-position tip.
    if (hasRealPhoto) maybeShowWallTip();
  }

  // Coach-mark: shown once per device the first time a wall photo lands so
  // users learn the frame is draggable AND that the rendered scale is an
  // estimate. Auto-hides on an 8s timer, on first pointerdown over the room
  // canvas, or via the × button. localStorage flag means it never shows
  // twice on the same device — bump the version suffix when copy changes
  // materially so previously-dismissed users still see the new message.
  const WALL_TIP_STORAGE_KEY = 'fy-wall-tip-seen-v2';
  const WALL_TIP_AUTO_HIDE_MS = 8000;
  const WALL_TIP_DRAG_DELAY_MS = 700; // ignore the mount's own pointer events

  function maybeShowWallTip() {
    const tipEl = document.getElementById('rmf-wall-tip');
    if (!tipEl) return;
    let seen = false;
    try { seen = localStorage.getItem(WALL_TIP_STORAGE_KEY) === '1'; } catch { /* private mode */ }
    if (seen) return;

    tipEl.hidden = false;
    // Force a reflow so the entrance transition runs (otherwise the class
    // change and the unhide land in the same frame and the fade is skipped).
    void tipEl.offsetWidth;
    tipEl.classList.add('pdp-wall-tip--visible');

    let dismissed = false;
    const stage = document.getElementById('rmf-room-preview-stage');
    const hideTip = () => {
      if (dismissed) return;
      dismissed = true;
      tipEl.classList.remove('pdp-wall-tip--visible');
      // After the fade-out finishes, fully hide so it doesn't catch clicks.
      setTimeout(() => { tipEl.hidden = true; }, 240);
      try { localStorage.setItem(WALL_TIP_STORAGE_KEY, '1'); } catch { /* noop */ }
      clearTimeout(autoTimer);
      tipEl.removeEventListener('click', onTipClick);
      stage?.removeEventListener('pointerdown', onStagePointer);
    };
    const onTipClick = (e) => {
      if (e.target.closest('.pdp-wall-tip__close')) hideTip();
    };
    // Delay the stage-pointerdown listener so the bundle's own mount-time
    // pointer events (e.g. orbit-controls setup) don't dismiss the tip the
    // moment it appears.
    const onStagePointer = () => hideTip();
    tipEl.addEventListener('click', onTipClick);
    setTimeout(() => {
      if (!dismissed) stage?.addEventListener('pointerdown', onStagePointer, { once: true });
    }, WALL_TIP_DRAG_DELAY_MS);
    const autoTimer = setTimeout(hideTip, WALL_TIP_AUTO_HIDE_MS);
  }

  // Keep the wall tab's label in sync with whether an analysis is loaded.
  // Safe to call any time — setUploadBusy(busy=true) overrides this with the
  // step label, and setUploadBusy(busy=false) re-runs us as final cleanup.
  function syncWallTabLabel() {
    const next = roomPreviewState.analysis
      ? T('gallery.your_wall', 'Your wall')
      : T('gallery.see_on_wall_cta', 'See it on your wall');
    document.querySelectorAll('.pdp-mode-tab[data-mode="wall"], .rmf-mode-tab[data-mode="wall"]').forEach(tab => {
      const label = tab.querySelector('.pdp-mode-tab__label, .rmf-mode-tab__label');
      if (label) label.textContent = next;
    });
  }

  // Loading state lives on the mode-pill's right segment so users keep the
  // Frame 3D view interactive while analysis runs. Label updates in-place —
  // no separate floating status text (it duplicated the pill label).
  function setUploadBusy(busy, text) {
    const heroPill = document.querySelector('.rmf-hero-mode-tabs');
    const wallTab  = heroPill?.querySelector('.pdp-mode-tab[data-mode="wall"]');
    const labelEl  = wallTab?.querySelector('.pdp-mode-tab__label');
    if (heroPill) heroPill.classList.toggle('rmf-hero-mode-tabs--busy', busy);
    if (wallTab) {
      wallTab.classList.toggle('pdp-mode-tab--busy', busy);
      wallTab.setAttribute('aria-busy', busy ? 'true' : 'false');
      if (labelEl && busy) labelEl.textContent = text || T('upload.analysing_wall', 'Analysing wall…');
    }
    if (!busy) syncWallTabLabel(); // restore "Your wall" / "See it on your wall"
  }

  async function startRoomFlow(file) {
    // Last upload wins — abort a still-running flow so two concurrent flows
    // can't interleave analysis writes, busy flags, and mounts.
    roomPreviewState.flowAbort?.abort();
    const flowAbort = new AbortController();
    roomPreviewState.flowAbort = flowAbort;
    const { signal } = flowAbort;
    try {
      setUploadBusy(true, T('upload.analysing_wall', 'Analysing wall…'));
      const dataUrl = await readFileAsDataUrl(file);
      if (signal.aborted) return;

      const analysis = await analyseRoom(dataUrl, (elapsed) => {
        const s = Math.round(elapsed);
        // Silent under ~12s (typical fast path); surface a counter after
        // that so the user has something concrete to look at on slow runs.
        const longLabel = T('upload.analysing_wall_seconds', 'Analysing wall · __SECONDS__s').replace('__SECONDS__', String(s));
        setUploadBusy(true, s >= 12 ? longLabel : T('upload.analysing_wall', 'Analysing wall…'));
      }, signal);
      if (signal.aborted) return;

      roomPreviewState.analysis = analysis;
      // Persist the analysis (NOT the per-product payload) so any PDP can
      // instantly restore "Your wall" without re-uploading.
      persistWallAnalysis(analysis);
      const payload = buildMountPayload(analysis);

      try {
        sessionStorage.setItem(SESSION_STORAGE_KEY, JSON.stringify(payload));
      } catch { /* sessionStorage full — non-fatal */ }

      setUploadBusy(true, 'Rendering…');
      await mountRoomPreview(payload);
      if (signal.aborted) return;
      setUploadBusy(false);
      updateUploadBtnLabel();
    } catch (err) {
      if (signal.aborted) return; // superseded — the newer flow owns the UI
      console.error('[RMF Room] flow failed', err);
      setUploadBusy(false);
      updateUploadBtnLabel();
      setPreviewStatus('error', 'Could not create preview. Try a different photo.');
    }
  }

  // Snapshot of the mutable configurator state at mount time. Used to
  // detect drift when the user re-opens the "In your room" thumb after
  // changing size / mount / glazing — we re-mount if anything changed.
  function currentConfigSignature() {
    return [
      state.sizeHandle || '',
      state.borderOption || '',
      state.glazingHandle || '',
      state.mountColorHandle || '',
      state.mountColorInnerHandle || '',
      state.frameColorHandle || '',
      state.orientation,
    ].join('|');
  }

  function buildMountPayload(analysis) {
    const sizePhysical = pickSizeForPreview();
    return {
      envmapUrl: analysis.envmapUrl,
      depthMapUrl: analysis.depthMapUrl,
      depthMaxM: analysis.depthMaxM,
      analysis: analysis.analysis,
      lighting: null, // no LLM lighting estimate on PDP; envmap only
      frameConfig: buildFrameConfigPayload(),
      // User's uploaded photo, if any. Frame-only PDP still allows
      // a "framed print" flow — when the customer uploads a photo we
      // render it inside the frame so they can see the final product.
      artworkUrl: currentArtwork.url,
      sizePhysical,
      roomPhotoUrl: analysis.roomPhotoUrl,
      // Keep the full uploaded photo. The bundle's smart wall-crop zooms in
      // on the focal wall (good for advisor mobile, wrong here) — we want
      // the user's original framing so they recognise their space.
      disableWallCrop: true,
      _sig: currentConfigSignature(),
    };
  }

  function buildFrameConfigPayload() {
    let profilePoints = null;
    if (data.profilePoints) {
      try {
        profilePoints = typeof data.profilePoints === 'string'
          ? JSON.parse(data.profilePoints) : data.profilePoints;
      } catch (e) {}
    }
    return {
      tile_url: (() => { const u = getFrameProp('mouldingTile'); return u ? (flippedTileUrlCache.get(u) || u) : null; })(),
      profile_width_mm: data.profileWidthMm || getFrameProp('mouldingWidth'),
      profile_depth_mm: data.profileDepthMm || 0,
      profile_points: profilePoints,
      lip_depth_normalized: data.lipDepthNormalized || 0,
      hex: currentFrameColorData()?.hex ?? data.frameHex,
      metalness: getFrameProp('metalness') ?? 0,
      roughness: getFrameProp('roughness') ?? 0.6,
      outer_face_material: data.outerFaceMaterial || 'tile',
      inner_face_material: data.innerFaceMaterial || 'tile',
      mountOption: state.borderOption || 'no-mount',
      borderWidth: MOUNT_BORDER_WIDTHS[state.borderOption] || 0,
      mountWidthMm: MOUNT_WIDTH_MM[state.borderOption] || 0,
      mountColor: mountHexFromHandle(state.mountColorHandle),
      ...(DOUBLE_MOUNT_TYPES.has(state.borderOption) ? { mountColorInner: mountHexFromHandle(state.mountColorInnerHandle) } : {}),
      includeGlazing: (state.glazingHandle || 'no-glaze') !== 'no-glaze',
    };
  }

  /**
   * Synthesise a payload for the default "generic wall" preview — a flat
   * front-facing wall at ~2.5m with the frame centred. No MapAnything,
   * no envmap, no depth map — just the frame on a blank wall. This gets
   * shown the moment the user opens the "In your room" thumb, so they
   * get an immediate sense of size + finish without uploading anything.
   * A photo upload later replaces this with the real room composite.
   */
  function buildDefaultPayload() {
    const sizePhysical = pickSizeForPreview();
    return {
      envmapUrl: null,
      depthMapUrl: null,
      depthMaxM: null,
      analysis: {
        // Square aspect matches the PDP's 1:1 media column — avoids the
        // letterboxing that would happen with a 4:3 scene in a square
        // container (which made the frame look much smaller than it is).
        image_dims: { width: 1200, height: 1200 },
        // Narrower "product-shot" FOV. Wider values read as a vast
        // panorama and shrink the frame to a postage stamp; 28° is
        // around a 85mm-equivalent focal length — close to what a
        // lifestyle photographer would use for a hero wall shot.
        intrinsics: { fov_y_deg: 28 },
        metric_scaling_factor: 1.0,
        // Single 3m × 3m wall centred 1.8m in front of the camera, rotated
        // 15° about the vertical axis. The tilt matters: head-on, a 15mm
        // moulding's chamfer is only a couple of screen pixels wide and
        // disappears into a flat rectangle. 15° turns it into a product-
        // shot 3/4 view where the moulding's profile reads clearly.
        // Real room composites pull the wall pose from MapAnything, so
        // this tilt only affects the default "generic wall" preview.
        //
        // Rotation about Y through the centroid (0, 0, 1.8):
        //   cos15° ≈ 0.9659, sin15° ≈ 0.2588
        //   corner (x, y, 1.8) → (x·cos, y, x·sin + 1.8)
        wall_planes: [{
          corners_3d: [
            [-1.4489, -1.5, 1.4117],
            [ 1.4489, -1.5, 2.1883],
            [ 1.4489,  1.5, 2.1883],
            [-1.4489,  1.5, 1.4117],
          ],
          normal:   [0.2588, 0, -0.9659], // rotated, still facing camera
          centroid: [0, 0, 1.8],
        }],
        pipeline_version: 'synthetic',
      },
      // Soft directional key-light from top-left (daylight-neutral) to give
      // the frame a natural drop shadow on the synthetic wall.
      // 3800K key light reads as warm-incandescent — closer to the
      // photographic warmth of the Gemini-baked hero raster. 5200K
      // (neutral daylight) made the live render look noticeably colder
      // than the static raster the user just left. Intensity 0.6 (down
      // from 0.9) tones down the directional bloom so frame highlights
      // don't blow out against the cream wall.
      lighting: { azimuth_deg: 40, elevation_deg: 35, kelvin: 3800, intensity: 0.6 },
      frameConfig: buildFrameConfigPayload(),
      artworkUrl: currentArtwork.url,
      sizePhysical,
      roomPhotoUrl: null,
      wallColor: frameMock?.wallColor || '#f5f0e8',
      _sig: currentConfigSignature(),
      _default: true,
    };
  }

  function mountHexFromHandle(handle) {
    if (!handle) return 0xfafaf8;
    const mc = MOUNT_COLORS.find(c => c.handle === handle);
    if (!mc) return 0xfafaf8;
    return parseInt(mc.hex.replace('#', ''), 16);
  }

  /**
   * Push the current configurator state into the mounted 3D preview.
   * Debounced so rapid changes (e.g. clicking through size buttons) don't
   * thrash setFrame's async texture reloads. No-op if the preview hasn't
   * been mounted yet — the first startRoomFlow() captures the initial
   * state.
   */
  // Singleton snapshot overlay used during config-driven 3D refreshes.
  // Holds the last good frame so the user never sees the empty/intermediate
  // canvas state the bundle paints while it rebuilds geometry + reloads
  // textures.
  //
  // PNG (not JPEG) is critical here. The 3D canvas renders with clearAlpha=0
  // — most pixels are transparent so the hero raster shows through as the
  // wall. JPEG has no alpha channel and would encode every transparent pixel
  // as solid black, turning the snapshot itself into the flash we're trying
  // to hide.
  let _refreshSnapshot = null;
  function captureCanvasAsPng() {
    return new Promise((resolve) => {
      requestAnimationFrame(() => requestAnimationFrame(() => {
        const threeCanvas = roomPreviewStage?.querySelector('canvas');
        if (!threeCanvas || !threeCanvas.width || !threeCanvas.height) {
          resolve(null);
          return;
        }
        const tmp = document.createElement('canvas');
        tmp.width = threeCanvas.width;
        tmp.height = threeCanvas.height;
        tmp.getContext('2d').drawImage(threeCanvas, 0, 0);
        resolve(tmp.toDataURL('image/png'));
      }));
    });
  }
  async function cloakDuringRefresh() {
    if (!roomPreviewStage) return;
    const dataUrl = await captureCanvasAsPng();
    if (!dataUrl) return;
    // If a previous refresh's snapshot is still in the DOM, drop it — only
    // the most recent capture should be visible.
    if (_refreshSnapshot) { _refreshSnapshot.remove(); _refreshSnapshot = null; }
    const img = document.createElement('img');
    img.src = dataUrl;
    img.alt = '';
    img.style.cssText = [
      'position:absolute', 'inset:0',
      'width:100%', 'height:100%',
      'object-fit:cover',
      'pointer-events:none',
      'z-index:5',
      'opacity:1',
      'transition:opacity 220ms ease',
    ].join(';');
    roomPreviewStage.appendChild(img);
    _refreshSnapshot = img;
  }
  function uncloakAfterRefresh() {
    const img = _refreshSnapshot;
    if (!img) return;
    _refreshSnapshot = null;
    // Two rAFs so the bundle's first post-update render has hit the screen
    // before the snapshot starts fading. Otherwise we'd cross-fade against
    // an empty canvas frame.
    requestAnimationFrame(() => requestAnimationFrame(() => {
      img.style.opacity = '0';
      setTimeout(() => img.remove(), 240);
    }));
  }

  let refreshRoomPreviewTimer = null;
  function refreshRoomPreview() {
    if (!roomPreviewState.instance) return;
    if (refreshRoomPreviewTimer) clearTimeout(refreshRoomPreviewTimer);
    refreshRoomPreviewTimer = setTimeout(async () => {
      refreshRoomPreviewTimer = null;
      // Snapshot the current canvas BEFORE the bundle starts rebuilding so
      // we can cover the brief flash (geometry teardown + texture reload)
      // with a static frame the user already saw.
      await cloakDuringRefresh();
      const _tileUrl = getFrameProp('mouldingTile');
      if (_tileUrl) await flipTileAsync(_tileUrl);
      const payload = roomPreviewState.analysis
        ? buildMountPayload(roomPreviewState.analysis)
        : buildDefaultPayload();
      roomPreviewState.lastPayload = payload;
      roomPreviewState.instance.update(payload).then(() => {
        // Frame geometry may have been rebuilt (new size / mount / colour) —
        // reapply measure lines so they attach to the new frameGroup.
        syncMeasurements3d();
        uncloakAfterRefresh();
      }, () => uncloakAfterRefresh());
      blitRoomThumb();
    }, 120);
  }

  async function ensureRoomPreviewMounted() {
    const sig = currentConfigSignature();
    const mounted = !!roomPreviewState.instance;
    const sigChanged = sig !== roomPreviewState.lastPayload?._sig;
    if (mounted && !sigChanged) {
      // Wall colour may have changed while the room view was hidden — sync it
      // without remounting (a remount would reset the user's orbit angle).
      if (roomPreviewState.lastPayload?._default) {
        applyRoomWallColor(frameMock?.wallColor || '#f5f0e8');
      }
      return;
    }

    const payload = roomPreviewState.analysis
      ? buildMountPayload(roomPreviewState.analysis)
      : buildDefaultPayload();
    await mountRoomPreview(payload);
    updateUploadBtnLabel();
  }

  function updateUploadBtnLabel() {
    if (!roomPreviewBtn) return;
    const hasAnalysis = !!roomPreviewState.analysis;
    // Post-upload the button shrinks to an icon-only "change photo"
    // affordance so it doesn't compete with the segmented view picker.
    roomPreviewBtn.classList.toggle('rmf-room-preview__upload-btn--icon-only', hasAnalysis);
    const label = roomPreviewBtn.querySelector('.rmf-upload-btn__label');
    if (label) label.textContent = hasAnalysis ? '' : T('upload.see_in_your_room', 'See in your room');
    roomPreviewBtn.setAttribute(
      'aria-label',
      hasAnalysis ? T('upload.change_room_photo', 'Change room photo') : T('upload.see_in_your_room', 'See in your room')
    );
  }

  if (roomPreviewBtn && roomPreviewInput) {
    roomPreviewBtn.addEventListener('click', () => roomPreviewInput.click());
  }
  if (roomPreviewInput) {
    roomPreviewInput.addEventListener('change', (ev) => {
      const file = ev.target.files?.[0];
      if (file) startRoomFlow(file);
      ev.target.value = ''; // allow re-selecting the same file
    });
  }

  // Restore the cached wall analysis (cross-PDP, cross-reload). If found,
  // flip the hero pill into its "Your wall is ready" state — change-photo
  // icon + divider visible, wall tab labelled "Your wall" instead of the
  // upload affordance. The 3D bundle still loads lazily; we don't mount
  // the room composite until the user actually clicks the Your wall tab.
  const persistedAnalysis = loadPersistedWallAnalysis();
  if (persistedAnalysis) {
    roomPreviewState.analysis = persistedAnalysis;
    const heroPill = document.querySelector('.rmf-hero-mode-tabs');
    if (heroPill) heroPill.classList.add('pdp-mode-tabs--has-wall');
    syncWallTabLabel();
  } else {
    // Fall back to the same-tab payload cache (legacy path).
    try {
      const cached = sessionStorage.getItem(SESSION_STORAGE_KEY);
      if (cached) {
        const payload = JSON.parse(cached);
        roomPreviewState.lastPayload = payload;
      }
    } catch { /* noop */ }
  }

  // Keep the 3D scene background in sync with the 2D wall colour picker.
  document.addEventListener('wall-color-change', (e) => {
    if (e.detail?.hex && roomPreviewState.lastPayload?._default !== false) {
      applyRoomWallColor(e.detail.hex);
    }
  });

  // ── 3D auto-activation ───────────────────────────────────────────────────
  //
  // The 3D scene replaces the static hero raster seamlessly once the bundle
  // and model have loaded. No user gesture required. If loading fails the
  // hero raster fades to the 2D canvas mock instead.

  function activateRoomMode() {
    if (!mockWrap || !roomPreviewEl) return;
    mockWrap.classList.add('rmf-mock-wrap--room-mode');
    // 3D-only chrome (rotate chip + side rotate buttons + the "3D" tab badge)
    // is gated on this class, so it never appears on the canvas-fallback path.
    mockWrap.classList.add('rmf-mock-wrap--3d-ready');
    roomPreviewEl.setAttribute('aria-hidden', 'false');
    // Keep the static hero raster visible — the 3D canvas renders with
    // alpha=0 (transparent backdrop, hidden shadow/AO quads) so the
    // cream-walled bake shows through and just the moulding/mount/glazing
    // meshes render on top. Fixes the bundle's default-dark-wall issue
    // on the live PDP without touching the bundle itself.
    if (heroImg) heroImg.style.opacity = '1';
  }

  function activateCanvasFallback() {
    // Make the hidden canvas mock visible without reflowing
    if (frameMock) frameMock.style.visibility = 'visible';
    // The CSS3D overlay was calibrated to the static hero raster; once the
    // canvas mock takes over it would double-render the user's photo.
    if (heroCss3d) heroCss3d.style.display = 'none';
    // Give the canvas one paint cycle to render before fading the hero out
    requestAnimationFrame(() => requestAnimationFrame(() => {
      if (heroImg) heroImg.style.opacity = '0';
    }));
  }

  // Defer the 3D bundle (three.js + room-3d-preview-bundle, ~700kb) until the
  // page has loaded (see autoBoot3D below) or the user switches mode, whichever
  // comes first. This keeps three.js out of the initial-paint / LCP critical
  // path. On success the 3D replaces the hero raster; on failure the hero
  // raster fades to the 2D canvas mock.
  let _3dBooted = false;
  const boot3D = () => {
    if (_3dBooted) return;
    _3dBooted = true;
    const currentTileUrl = getFrameProp('mouldingTile');
    // Defer payload selection until just before the mount: if the user
    // clicks "Your wall" while the bundle is still downloading, we want
    // to pick up that intent (analysis + heroMode === 'wall') and mount
    // the real-room composite instead of the synthetic wall.
    loadPreviewBundle()
      .then(() => currentTileUrl ? flipTileAsync(currentTileUrl) : Promise.resolve())
      .then(() => {
        const wantWall = mockWrap?.dataset.heroMode === 'wall'
          && !!roomPreviewState.analysis;
        return mountRoomPreview(wantWall
          ? buildMountPayload(roomPreviewState.analysis)
          : buildDefaultPayload());
      })
      .then(() => activateRoomMode())
      .catch(() => activateCanvasFallback());
  };

  // Auto-boot the 3D scene, but only once the page is fully loaded AND the main
  // thread is idle — so fetching three.js + the ~700kb bundle never competes
  // with first paint, LCP, or the user's own interactions:
  //   1. wait for `window.load` (all critical resources fetched + parsed), then
  //   2. wait for an idle window via requestIdleCallback — the callback won't
  //      run while the browser is busy handling input, so it can't jank a
  //      scroll or tap. A 3s timeout caps the wait so a perpetually-busy page
  //      still warms the scene; Safari (no rIC) falls back to a 1s post-load
  //      delay, by which point the page has settled.
  // The 3D frame then renders on its own — the user no longer has to press the
  // Frame/3D toggle. boot3D() is idempotent (the _3dBooted guard), so a manual
  // mode switch before this fires just wins the race harmlessly.
  const autoBoot3D = () => {
    const whenIdle = window.requestIdleCallback
      ? cb => window.requestIdleCallback(cb, { timeout: 3000 })
      : cb => setTimeout(cb, 1000);
    whenIdle(() => boot3D());
  };
  if (document.readyState === 'complete') {
    autoBoot3D();
  } else {
    window.addEventListener('load', autoBoot3D, { once: true });
  }

  // bfcache restore (view cart → Back): the snapshot freezes _3dBooted = true,
  // but the browser may have discarded the WebGL context during the freeze —
  // leaving a blank stage with nothing that would ever re-boot it (`load`
  // doesn't re-fire on bfcache restores, and boot3D() is gated on _3dBooted).
  // Dispose whatever was restored and boot a fresh scene the same idle-gated
  // way as the initial load; the bundle itself survives in the restored heap,
  // so this is a cheap re-mount, not a re-download.
  window.addEventListener('pageshow', (event) => {
    if (!event.persisted || !_3dBooted) return;
    try { roomPreviewState.instance?.dispose?.(); } catch { /* noop */ }
    roomPreviewState.instance = null;
    roomPreviewState.lastPayload = null;
    _3dBooted = false;
    autoBoot3D();
  });

  // Side rotate buttons — drive the same yaw rotation as click-and-drag on the
  // 3D canvas. The bundle's controls aren't exposed on the public API, so we
  // simulate a horizontal pointer drag (~120px) on the canvas, which the
  // bundle's pointer listeners pick up as a rotate gesture. Buttons are hidden
  // until 3D loads (CSS gate on .rmf-mock-wrap--3d-ready), so this is no-op
  // in the canvas-fallback path.
  function simulateCanvasDrag(direction) {
    // Prefer the live renderer canvas if the bundle has mounted; fall back
    // to whatever's in the stage (no-op in canvas-fallback path since the
    // buttons are gated on the 3d-ready class anyway).
    const canvas = window.Room3DPreview?.viewer?.renderer?.domElement
      || roomPreviewState.instance?.viewer?.renderer?.domElement
      || roomPreviewStage?.querySelector('canvas');
    if (!canvas) return;
    const rect = canvas.getBoundingClientRect();
    const cx = rect.left + rect.width / 2;
    const cy = rect.top + rect.height / 2;
    // Small discrete nudge per click rather than a full sweep. Total drag
    // distance the bundle's controls translate linearly to rotation delta.
    const dx = direction === 'left' ? -18 : 18;
    const pointerId = Math.floor(Math.random() * 1e6);
    const opts = (x, y, buttons) => ({
      pointerId, pointerType: 'mouse', isPrimary: true,
      clientX: x, clientY: y, screenX: x, screenY: y,
      button: 0, buttons,
      bubbles: true, cancelable: true,
    });
    // The bundle's pointerdown handler calls setPointerCapture synchronously,
    // which throws InvalidStateError for synthesized pointers (no real pointer
    // is associated with our pointerId). The throw aborts the rest of the
    // handler — including the registration of pointermove/pointerup listeners —
    // so we stub setPointerCapture while we drive the drag.
    const origCapture = canvas.setPointerCapture;
    const origRelease = canvas.releasePointerCapture;
    canvas.setPointerCapture = () => {};
    canvas.releasePointerCapture = () => {};
    canvas.dispatchEvent(new PointerEvent('pointerdown', opts(cx, cy, 1)));
    // Dispatch the move sequence synchronously. The bundle's controls accumulate
    // delta from each pointermove, so several stepped moves give a smooth-looking
    // camera arc without depending on rAF (which CDP-driven testing can stall).
    const STEPS = 8;
    for (let i = 1; i <= STEPS; i++) {
      const x = cx + (dx * i) / STEPS;
      canvas.dispatchEvent(new PointerEvent('pointermove', opts(x, cy, 1)));
    }
    canvas.dispatchEvent(new PointerEvent('pointerup', opts(cx + dx, cy, 0)));
    canvas.setPointerCapture = origCapture;
    canvas.releasePointerCapture = origRelease;
  }

  document.getElementById('rmf-hero-rotate-left')?.addEventListener('click', () => simulateCanvasDrag('left'));
  document.getElementById('rmf-hero-rotate-right')?.addEventListener('click', () => simulateCanvasDrag('right'));

  // ── Related product grids ────────────────────────────────────────────────

  function escHtml(s) {
    return String(s).replace(/&/g,'&amp;').replace(/"/g,'&quot;').replace(/</g,'&lt;').replace(/>/g,'&gt;');
  }

  function fmtPrice(cents) {
    // Route through fmt() so related-product card prices match the headline
    // price's decimal style (Shopify money_format-driven, not Intl-driven).
    return fmt((cents || 0) / 100);
  }

  // Render a product card using the current product's frame config.
  // The mock shape/color matches this PDP; only the artwork image differs.
  function buildFrameMockCard(product) {
    const artworkUrl = product.featured_image
      ? product.featured_image.replace(/(\.\w+)$/, '_600x$1')
      : '';
    const profilePointsAttr = data.profilePoints
      ? `data-profile-points="${escHtml(typeof data.profilePoints === 'string' ? data.profilePoints : JSON.stringify(data.profilePoints))}"`
      : '';
    const mouldingTileAttr = data.mouldingTile
      ? `data-moulding-tile="${escHtml(data.mouldingTile)}"`
      : '';
    return `
      <a href="/products/${product.handle}" class="rmf-related-card">
        <div class="rmf-related-card__image">
          <gallery-wall-frame-mock
            data-frame-color="${escHtml(data.frameHex || '#1a1a1a')}"
            ${mouldingTileAttr}
            data-moulding-width="${escHtml(String(data.mouldingWidth || 15))}"
            ${profilePointsAttr}
            data-border-option="${escHtml(data.borderOption || 'no-border')}"
            data-border-width="${data.borderWidth || 0}"
            data-mount-color="${escHtml(data.mountColorHex || '#fafaf8')}"
            data-artwork-url="${escHtml(artworkUrl)}"
            data-artwork-width="12"
            data-artwork-aspect="1.0"
            class="rmf-grid-mock"
          ><canvas class="gallery-wall-frame-mock__canvas"></canvas></gallery-wall-frame-mock>
        </div>
        <p class="rmf-related-card__title">${escHtml(product.title)}</p>
        <p class="rmf-related-card__price">From ${fmtPrice(product.price_min)}</p>
      </a>
    `;
  }

  // Apply the stored user photo to all grid mocks (range + similar).
  function applyPhotoToGridMocks(imageUrl) {
    const url = imageUrl || (() => {
      try { return JSON.parse(localStorage.getItem(LS_KEY) || '{}').imageUrl || ''; } catch(e) { return ''; }
    })();
    if (!url) return;
    document.querySelectorAll('.rmf-grid-mock').forEach(mock => mock.setArtwork?.(url));
  }

  // Reset grid mocks to their default product image.
  function clearPhotoFromGridMocks() {
    document.querySelectorAll('.rmf-grid-mock').forEach(mock => {
      if (mock.dataset.artworkUrl) {
        mock.setArtwork?.(mock.dataset.artworkUrl);
      } else {
        mock.artworkImage = null;
        mock.artworkUrl = '';
        mock.scheduleRender?.();
      }
    });
  }

  // ── Photo upload ──────────────────────────────────────────────────────────

  const LS_KEY = 'rmf:user-photo';

  function applyPhotoToMock(imageUrl, width, height) {
    const photoAspect = (width && height) ? width / height : null;
    // Don't pass photoAspect to setArtwork — the 2D mock's artworkAspect
    // is the PAPER aspect (driven by the size picker via updateConfig),
    // not the photo's. drawImageCover handles photo fit from the image
    // texture directly. Passing here would override the paper aspect and
    // render the frame shaped to the photo instead of the selected size.
    if (frameMock) frameMock.setArtwork?.(imageUrl);

    // Overlay user photo into the CSS3D aperture on the hero raster
    if (heroCss3d && heroImg) {
      let artworkImg = heroCss3d.querySelector('img.rmf-hero-artwork');
      if (imageUrl) {
        if (!artworkImg) {
          artworkImg = document.createElement('img');
          artworkImg.className = 'rmf-hero-artwork';
          artworkImg.style.cssText = 'object-fit:cover;';
          heroCss3d.appendChild(artworkImg);
        }
        artworkImg.src = imageUrl;
        // Re-trigger transform now that the img is populated
        heroCss3d.parentElement?.dispatchEvent(new CustomEvent('artwork-changed', { bubbles: false }));
      } else if (artworkImg) {
        artworkImg.remove();
      }
    }

    currentArtwork.url = imageUrl || null;
    currentArtwork.aspect = photoAspect;
    refreshRoomPreview();
  }

  const sizeDimsMap = {};
  (data.sizeOptions || []).forEach(s => {
    let w = s.widthIn, h = s.heightIn;
    if (!w || !h) {
      const parts = s.handle.split('-x-');
      if (parts.length === 2) { w = parseFloat(parts[0]); h = parseFloat(parts[1]); }
    }
    sizeDimsMap[s.handle] = { w, h };
  });

  let uploadedPhotoDimensions = null;

  const MIN_PRINT_DPI = 100;      // hard block — below this Prodigi output is unacceptable
  const MARGINAL_PRINT_DPI = 150; // soft warning — recommended minimum for sharp prints

  function checkCroppedResolution(photoW, photoH, printW, printH, dpi) {
    const targetAspect = printH / printW;
    const photoAspect  = photoH / photoW;
    let croppedW, croppedH;
    if (photoAspect > targetAspect) {
      croppedW = photoW;
      croppedH = photoW * targetAspect;
    } else {
      croppedH = photoH;
      croppedW = photoH / targetAspect;
    }
    return croppedW >= printW * dpi && croppedH >= printH * dpi;
  }

  // Returns 'ok' | 'marginal' | 'blocked'
  function photoResolutionForSize(sizeHandle) {
    if (!uploadedPhotoDimensions) return 'ok';
    const { width: pw, height: ph } = uploadedPhotoDimensions;
    const dims = sizeDimsMap[sizeHandle];
    if (!dims || !dims.w || !dims.h) return 'ok';
    const { w: fw, h: fh } = dims;

    // Check both photo orientations — user may rotate to fit the frame
    const meetsMin = checkCroppedResolution(pw, ph, fw, fh, MIN_PRINT_DPI)
                  || checkCroppedResolution(ph, pw, fw, fh, MIN_PRINT_DPI);
    if (!meetsMin) return 'blocked';

    const meetsRecommended = checkCroppedResolution(pw, ph, fw, fh, MARGINAL_PRINT_DPI)
                          || checkCroppedResolution(ph, pw, fw, fh, MARGINAL_PRINT_DPI);
    return meetsRecommended ? 'ok' : 'marginal';
  }

  function getQualityEl(btn) {
    return btn.querySelector('.rmf-size-btn__quality');
  }

  function applyResolutionFilter() {
    if (!uploadedPhotoDimensions) {
      const bannerEl = document.getElementById('rmf-size-quality-banner');
      if (bannerEl) bannerEl.hidden = true;
      return;
    }
    // Find the largest size (by area) where the photo meets the recommended DPI.
    // Sizes below that threshold stay fully selectable — no disabled state.
    let maxOkAreaIn2 = 0;
    let maxOkBtn = null;

    document.querySelectorAll('.rmf-size-btn[data-size-handle]').forEach(btn => {
      btn.classList.remove('rmf-size-btn--resolution-blocked', 'rmf-size-btn--resolution-marginal');
      const qualityEl = getQualityEl(btn);
      if (qualityEl) qualityEl.textContent = '';
      if (btn.classList.contains('rmf-size-btn--aspect-mismatch')) return;

      const quality = photoResolutionForSize(btn.dataset.sizeHandle);
      if (quality === 'ok') {
        const wIn = parseFloat(btn.dataset.widthIn) || 0;
        const hIn = parseFloat(btn.dataset.heightIn) || 0;
        const area = wIn * hIn;
        if (area > maxOkAreaIn2) { maxOkAreaIn2 = area; maxOkBtn = btn; }
      }
    });

    // Show banner only when some sizes exceed the recommended threshold.
    const bannerEl = document.getElementById('rmf-size-quality-banner');
    const bannerTextEl = document.getElementById('rmf-size-quality-banner-text');
    const allOk = !document.querySelector('.rmf-size-btn[data-size-handle]:not(.rmf-size-btn--aspect-mismatch)')
      || [...document.querySelectorAll('.rmf-size-btn[data-size-handle]:not(.rmf-size-btn--aspect-mismatch)')]
          .every(btn => photoResolutionForSize(btn.dataset.sizeHandle) === 'ok');

    if (bannerEl && bannerTextEl && !allOk && maxOkBtn) {
      const dim = formatBtnDim(maxOkBtn, displayUnit);
      bannerTextEl.textContent = T('upload.best_print_quality_up_to', 'Best print quality up to {size} with your photo — larger sizes may appear slightly soft.').replace('{size}', dim);
      bannerEl.hidden = false;
    } else if (bannerEl) {
      bannerEl.hidden = true;
    }

    applyBucketDisabledFilter();
  }

  function clearResolutionFilter() {
    document.querySelectorAll('.rmf-size-btn--resolution-blocked, .rmf-size-btn--resolution-marginal').forEach(btn => {
      btn.classList.remove('rmf-size-btn--resolution-blocked', 'rmf-size-btn--resolution-marginal');
      const qualityEl = getQualityEl(btn);
      if (qualityEl) qualityEl.textContent = '';
      if (!btn.classList.contains('rmf-size-btn--aspect-mismatch')) btn.title = '';
    });
    const bannerEl = document.getElementById('rmf-size-quality-banner');
    if (bannerEl) bannerEl.hidden = true;
    applyBucketDisabledFilter();
  }

  function sizeMatchesAspect(imgW, imgH, sizeW, sizeH) {
    if (!imgW || !imgH || !sizeW || !sizeH) return true;
    const imgRatio  = Math.max(imgW, imgH) / Math.min(imgW, imgH);
    const sizeRatio = Math.max(sizeW, sizeH) / Math.min(sizeW, sizeH);
    return Math.abs(imgRatio - sizeRatio) / sizeRatio <= 0.18;
  }

  function isSizeBlocked(btn) {
    return btn.classList.contains('rmf-size-btn--aspect-mismatch')
        || btn.classList.contains('rmf-size-btn--sold-out');
  }

  // Disable a bucket button when every size it contains is blocked. Runs
  // after each filter pass so the bucket row mirrors what's actually
  // selectable in the size grid below. If the active bucket gets disabled,
  // hand off to the bucket that owns the currently-selected size.
  function applyBucketDisabledFilter() {
    const bucketRowEl = document.getElementById('rmf-bucket-row');
    if (!bucketRowEl) return;
    let activeBecameDisabled = false;
    bucketRowEl.querySelectorAll('.rmf-bucket').forEach(bucketBtn => {
      const bucketId = bucketBtn.dataset.bucket;
      if (!bucketId) return;
      const sizes = document.querySelectorAll(`.rmf-size-btn[data-bucket="${bucketId}"]`);
      if (!sizes.length) return;
      const allBlocked = [...sizes].every(isSizeBlocked);
      bucketBtn.classList.toggle('rmf-bucket--disabled', allBlocked);
      bucketBtn.setAttribute('aria-disabled', allBlocked ? 'true' : 'false');
      if (allBlocked && bucketBtn.classList.contains('rmf-bucket--active')) {
        activeBecameDisabled = true;
      }
    });
    if (activeBecameDisabled && state.sizeHandle) {
      syncActiveBucketToSize(state.sizeHandle);
    }
  }

  // Sync the active bucket button to the bucket of the currently selected
  // size. Used when autoSelectCompatibleSize jumps the user to a size in a
  // different bucket — the bucket-row UI must follow.
  function syncActiveBucketToSize(sizeHandle) {
    const bucketRowEl = document.getElementById('rmf-bucket-row');
    if (!bucketRowEl) return;
    const activeSizeBtn = document.querySelector(`.rmf-size-btn[data-size-handle="${sizeHandle}"]`);
    const bucketId = activeSizeBtn?.dataset.bucket;
    if (!bucketId) return;
    bucketRowEl.querySelectorAll('.rmf-bucket').forEach(b => {
      b.classList.toggle('rmf-bucket--active', b.dataset.bucket === bucketId);
    });
    const sizeButtonsEl = document.getElementById('rmf-size-buttons');
    if (sizeButtonsEl) sizeButtonsEl.dataset.activeBucket = bucketId;
  }

  function applySizeAspectFilter(imgWidth, imgHeight) {
    const allBtns = [...document.querySelectorAll('.rmf-size-btn')];
    allBtns.forEach(btn => {
      const dims = sizeDimsMap[btn.dataset.sizeHandle] || {};
      const mismatch = !sizeMatchesAspect(imgWidth, imgHeight, dims.w, dims.h);
      btn.classList.toggle('rmf-size-btn--aspect-mismatch', mismatch);
      btn.title = mismatch ? "Size doesn't match your photo's proportions" : '';
    });

    // Safety valve: if the aspect filter would lock ALL tier-mode size buttons
    // (e.g. user uploaded a square photo but only rectangular sizes are available),
    // clear the filter entirely rather than leaving the picker fully disabled.
    const tierBtns = allBtns.filter(b => b.dataset.sizeHandle);
    const allMismatched = tierBtns.length > 0
      && tierBtns.every(b => b.classList.contains('rmf-size-btn--aspect-mismatch'));
    if (allMismatched) {
      tierBtns.forEach(b => {
        b.classList.remove('rmf-size-btn--aspect-mismatch');
        b.title = '';
      });
    }

    // Apply resolution filter after aspect (skips aspect-mismatched buttons)
    applyResolutionFilter();
    // If the active size is now blocked, move to the largest still-compatible size
    autoSelectCompatibleSize();
  }

  function autoSelectCompatibleSize() {
    if (!isTierMode) return;
    const activeBtn = document.querySelector(`.rmf-size-btn[data-size-handle="${state.sizeHandle}"]`);
    if (!activeBtn || !isSizeBlocked(activeBtn)) return;

    // Sort all non-blocked sizes by print area (largest first)
    const best = [...document.querySelectorAll('.rmf-size-btn[data-size-handle]')]
      .filter(btn => !isSizeBlocked(btn))
      .map(btn => {
        const dims = sizeDimsMap[btn.dataset.sizeHandle] || {};
        return { handle: btn.dataset.sizeHandle, area: (dims.w || 0) * (dims.h || 0) };
      })
      .sort((a, b) => b.area - a.area)[0];

    if (best) {
      selectSizeOption(best.handle);
      syncActiveBucketToSize(best.handle);
    }
  }

  function clearSizeAspectFilter() {
    document.querySelectorAll('.rmf-size-btn--aspect-mismatch').forEach(btn => {
      btn.classList.remove('rmf-size-btn--aspect-mismatch');
      btn.title = '';
    });
    clearResolutionFilter();
  }

  function savePhotoToStorage(imageUrl, width, height) {
    try {
      localStorage.setItem(LS_KEY, JSON.stringify({ imageUrl, width, height, ts: Date.now() }));
    } catch (e) { /* storage full */ }
  }

  function clearPhotoFromStorage() {
    try { localStorage.removeItem(LS_KEY); } catch (e) {}
  }

  function restorePhotoFromStorage() {
    try {
      const raw = localStorage.getItem(LS_KEY);
      if (!raw) return;
      const { imageUrl, width, height } = JSON.parse(raw);
      if (!imageUrl) return;

      // Bridge localStorage (PDP, persists across sessions) → sessionStorage
      // (photo-upload-component, wiped each session). Without this mirror the
      // form widget shows its empty "Upload your photo" state on a fresh
      // session even though we've already painted the persisted photo onto
      // the mock. The photo-upload script is deferred and parsed after this
      // one, so writing here lands before its connectedCallback runs and
      // calls #restoreUploadedPhoto. Timestamp is refreshed to bypass the
      // component's 1-hour sessionStorage expiry.
      try {
        if (!sessionStorage.getItem('uploaded_photo')) {
          const fileType = imageUrl.startsWith('data:image/png') ? 'image/png'
                         : imageUrl.startsWith('data:image/webp') ? 'image/webp'
                         : 'image/jpeg';
          sessionStorage.setItem('uploaded_photo', JSON.stringify({
            dataUrl: imageUrl,
            fileName: 'photo.jpg',
            fileSize: 0,
            fileType,
            width: width || 0,
            height: height || 0,
            timestamp: Date.now(),
          }));
        }
      } catch (e) { /* sessionStorage full — form widget will stay empty, mock still paints below */ }

      // Paint the mock immediately so the user sees the photo without waiting
      // for the component's async restore (image decode → blob → crop). The
      // component will fire photo-upload:changed once it finishes, which our
      // listener uses to apply the size-aspect filter.
      // Persisted landscape photo: set orientation up-front so the first
      // formatBtnDim / mock paint at boot is already landscape (no flash).
      // Direct state write (not applyOrientation) — the full re-render runs
      // when the component later fires photo-upload:changed.
      if (width && height && width > height) {
        state.orientation = 'landscape';
        document.body.dataset.frameOrientation = 'landscape';
      }
      applyPhotoToMock(imageUrl, width, height);
      applyPhotoToGridMocks(imageUrl);
    } catch (e) {}
  }

  // Convert a blob: URL to a data: URL so we hold an independent copy of the
  // bytes. The photo-upload component revokes its blob URL when the photo is
  // replaced or it tears down, which leaves stale references in our state and
  // the 3D bundle's TextureLoader (ERR_FILE_NOT_FOUND on subsequent re-mounts,
  // e.g. when the user uploads a wall photo after the artwork). For non-blob
  // URLs (data:, https:) this is a no-op.
  async function persistImageUrl(url) {
    if (!url || !url.startsWith('blob:')) return url;
    try {
      const blob = await fetch(url).then(r => r.blob());
      return await new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = () => resolve(reader.result);
        reader.onerror = () => reject(reader.error || new Error('FileReader failed'));
        reader.readAsDataURL(blob);
      });
    } catch (err) {
      console.warn('[RMF] persistImageUrl failed; passing original URL through', err);
      return url;
    }
  }

  // Listen for photo-upload:changed fired by PhotoUploadComponent
  document.addEventListener('photo-upload:changed', async (e) => {
    const { imageUrl: rawImageUrl, width, height } = e.detail || {};
    if (!rawImageUrl) return;
    const imageUrl = await persistImageUrl(rawImageUrl);
    uploadedPhotoDimensions = (width && height) ? { width, height } : null;
    // Landscape photo → rotate the frame to landscape. Square / portrait → portrait.
    applyOrientation(width && height && width > height ? 'landscape' : 'portrait');
    applyPhotoToMock(imageUrl, width, height);
    applyPhotoToGridMocks(imageUrl);
    applySizeAspectFilter(width, height); // also calls applyResolutionFilter internally
    savePhotoToStorage(imageUrl, width, height);
    hasPhoto = true;
    document.getElementById('rmf-size-buttons')?.classList.add('rmf-has-photo');
    document.dispatchEvent(new CustomEvent('rmf:photo-uploaded', { detail: { productId: data.productId, hasPhoto: true } }));

    // QA #108: pre-upload the photo to S3 now, in the background, so Add-to-cart
    // doesn't block ~15s on it at submit time. uploadToS3() caches by image URL,
    // so the ATC handler's await returns this same result instantly. Fire and
    // forget — failures are retried when ATC calls uploadToS3() again.
    const photoUploadEl = e.target instanceof Element ? e.target.closest('photo-upload-component') : null;
    (photoUploadEl || document.querySelector('photo-upload-component'))?.uploadToS3?.().catch(() => {
      // Background upload failed; the photo is still usable and ATC will retry.
      // Clear any progress label back to "ready".
      if (photoSummaryEl) photoSummaryEl.textContent = T('upload.photo_ready', 'Photo ready');
      photoStackRow?.classList.remove('rmf-stack-row--uploading');
    });
    // Load art pricing and refresh price display once loaded
    loadArtPricing().then(() => {
      refreshPriceDisplay();
      populateSizePrices();
      updatePhotoRowDelta();
    });
    // Update photo row state
    if (photoSummaryEl) photoSummaryEl.textContent = T('upload.photo_ready', 'Photo ready');
    if (photoStackRow) photoStackRow.classList.add('rmf-stack-row--has-photo');
  });

  // QA #108 progress: surface the background S3 upload on the photo row so the
  // wait is visible and ATC feels instant once the row reads "Photo ready".
  // Driven by the photo-upload component's real XHR upload-progress events; a
  // cached / already-uploaded photo emits none, so the row just stays "ready".
  document.addEventListener('photo-upload:s3-status', (e) => {
    if (!photoSummaryEl) return;
    const { status, percent } = e.detail || {};
    if (status === 'started' || status === 'progress') {
      const label = T('upload.uploading', 'Uploading photo');
      photoSummaryEl.textContent = typeof percent === 'number' ? `${label} ${percent}%` : `${label}…`;
      photoStackRow?.classList.add('rmf-stack-row--uploading');
    } else if (status === 'completed') {
      photoSummaryEl.textContent = T('upload.photo_ready', 'Photo ready');
      photoStackRow?.classList.remove('rmf-stack-row--uploading');
    }
  });

  // Listen for photo removal
  document.addEventListener('photo-upload:removed', () => {
    clearPhotoFromStorage();
    clearPhotoFromGridMocks();
    if (frameMock) {
      frameMock.artworkImage = null;
      frameMock.artworkUrl = '';
      frameMock.scheduleRender?.();
    }
    heroCss3d?.querySelector('img.rmf-hero-artwork')?.remove();
    currentArtwork.url = null;
    currentArtwork.aspect = null;
    hasPhoto = false;
    applyOrientation('portrait');
    document.getElementById('rmf-size-buttons')?.classList.remove('rmf-has-photo');
    uploadedPhotoDimensions = null;
    refreshRoomPreview();
    refreshPriceDisplay();
    populateSizePrices();
    clearSizeAspectFilter();
    if (photoSummaryEl) photoSummaryEl.textContent = `(${T('stack.optional', 'Optional')})`;
    if (photoStackRow) photoStackRow.classList.remove('rmf-stack-row--has-photo');
  });


  // ── ATC submit ───────────────────────────────────────────────────────────
  // Single button. With no photo, the form posts the frame variant directly
  // to /cart/add (default browser behaviour). With a photo present we
  // intercept, upload to S3, and POST the framed-print art variant with all
  // frame properties to /cart/add.js so frame + print land in the cart as a
  // bundle.
  const frameForm = document.getElementById('rmf-form-frame');
  const buyFrameBtn = document.getElementById('rmf-buy-frame-btn');

  // QA #57: after a framed-print ATC the button is disabled + relabelled
  // ("Uploading photo…" / "Adding…"). If the buyer then navigates to the cart
  // and presses Back, the page is restored from the bfcache with that frozen
  // in-flight state intact (the snapshot predates the submit handler's finally
  // reset). Re-enable + restore the original label on a persisted pageshow.
  if (buyFrameBtn) {
    const buyLabelEl = buyFrameBtn.querySelector('.rmf-btn__label');
    const initialBuyLabel = buyLabelEl ? buyLabelEl.textContent : '';
    window.addEventListener('pageshow', (event) => {
      if (!event.persisted) return;
      buyFrameBtn.disabled = false;
      if (buyLabelEl) buyLabelEl.textContent = initialBuyLabel;
    });
  }

  // Display properties — make the configurator selection legible on the cart
  // line. The cart-products snippet renders `properties[Size|Frame|Mount|Glazing]`
  // as a subtitle; the underscore-prefixed handles stay hidden. Build labels
  // from the live sticky-CTA sub line (already kept in sync by the bucket /
  // colour / mount / size handlers) and append them as hidden inputs at submit
  // time so they survive both the no-photo (browser-default POST) and the
  // with-photo (cart/add.js fetch) paths.
  function readDisplayProps() {
    const txt = (id) => document.getElementById(id)?.textContent.trim() || '';
    const bucket = txt('rmf-sticky-bucket');
    const size   = txt('rmf-sticky-size-label');
    const sizeLabel = [bucket, size].filter(Boolean).join(' ');
    const colorName = txt('rmf-sticky-color-name');
    const productTitle = (data.productTitle || document.querySelector('.rmf-title')?.textContent || '').trim();
    // "Pure — Green Frame" is more useful than "Green" alone, but we don't
    // want to repeat the colour twice — fall back to the product title only
    // when the sticky's colour cell is empty.
    const frameLabel = colorName || productTitle;
    const mountLabel = txt('rmf-sticky-mount-name');
    const glazingLabelMap = {
      acrylic: 'Acrylic',
      'art-glass': 'Art glass',
      'museum-glaze': 'Museum glaze',
      'no-glaze': 'No glaze',
    };
    const glazingLabel = glazingLabelMap[state.glazingHandle] || (state.glazingHandle || '').replace(/-/g, ' ');
    return { sizeLabel, frameLabel, mountLabel, glazingLabel };
  }

  function ensureFormInput(form, name, value) {
    if (!form) return;
    let input = form.querySelector(`input[type="hidden"][name="${name}"]`);
    if (!value) {
      if (input) input.remove();
      return;
    }
    if (!input) {
      input = document.createElement('input');
      input.type = 'hidden';
      input.name = name;
      form.appendChild(input);
    }
    input.value = value;
  }

  function syncDisplayPropsToForm() {
    if (!frameForm) return;
    const { sizeLabel, frameLabel, mountLabel, glazingLabel } = readDisplayProps();
    ensureFormInput(frameForm, 'properties[Size]', sizeLabel);
    ensureFormInput(frameForm, 'properties[Frame]', frameLabel);
    ensureFormInput(frameForm, 'properties[Mount]', mountLabel);
    ensureFormInput(frameForm, 'properties[Glazing]', glazingLabel);
  }

  if (frameForm) {
    // Run before the existing async submit handler (capture phase) so the
    // visible properties land on both submission paths.
    frameForm.addEventListener('submit', syncDisplayPropsToForm, { capture: true });
  }

  if (frameForm && buyFrameBtn && data.artVariantsByOrientation) {
    frameForm.addEventListener('submit', async (e) => {
      if (!hasPhoto) return;
      const photoUpload = document.querySelector('photo-upload-component');
      const hasUpload = photoUpload && (photoUpload.uploadedFile || photoUpload.croppedImageURL || photoUpload.uploadedImageURL);
      if (!hasUpload) return;

      e.preventDefault();

      const labelEl = buyFrameBtn.querySelector('.rmf-btn__label');
      const savedLabel = labelEl ? labelEl.textContent : '';
      buyFrameBtn.disabled = true;
      if (labelEl) labelEl.textContent = 'Uploading photo…';

      try {
        const s3Data = await photoUpload.uploadToS3();
        if (!s3Data) throw new Error('S3 upload returned null');
        const s3Key = typeof s3Data === 'string' ? s3Data : s3Data.key;
        const s3Url = typeof s3Data === 'object' ? s3Data.url : null;

        const artEntry = artEntryFor(state.sizeHandle);
        const artSizeHandle = artEntry?.artSizeHandle || state.sizeHandle;

        // Art variant + size follow the FRAME's active orientation — the
        // print must match the rotated frame, not the rm_tier portrait size.
        const artVariantId = artEntry?.artVariantId
          || data.artVariantsByOrientation[state.orientation]
          || Object.values(data.artVariantsByOrientation)[0];

        const { sizeLabel, frameLabel, mountLabel, glazingLabel } = readDisplayProps();
        const comboId = crypto.randomUUID();
        const artProps = {
          _readymade_combo_id: comboId,
          _size_option:        artSizeHandle,
          _framing_option:     'unframed',
          _border_option:      'no-border',
          _artwork_s3_key:     s3Key,
        };
        if (sizeLabel) artProps['Size'] = sizeLabel;
        if (s3Url)     artProps['_uploaded_photo_url'] = s3Url;

        // Prodigi fulfillment properties — mirror what sku-picker adds for the normal PDP flow
        const prodigiCfg = lookupProdigiConfig(artSizeHandle, 'unframed', 'no-border', 'art-print');
        if (prodigiCfg.prodigi_sku)    artProps['_prodigi_sku']     = prodigiCfg.prodigi_sku;
        if (prodigiCfg.prodigi_wrap)   artProps['_prodigi_wrap']    = prodigiCfg.prodigi_wrap;
        if (prodigiCfg.border_percent) artProps['_border_percent']  = prodigiCfg.border_percent;
        if (prodigiCfg.warning)        artProps['_prodigi_warning'] = prodigiCfg.warning;

        const frameProps = {
          _readymade_combo_id: comboId,
          _readymade_frame:    'true',
          _size_option:        artSizeHandle,
          _border_option:      state.borderOption || '',
          _glazing_option:     state.glazingHandle || '',
          _frame_color:        state.frameColorHandle || data.frameColorHandle || '',
        };
        if (state.mountColorHandle)      frameProps['_mount_color']       = state.mountColorHandle;
        if (state.mountColorInnerHandle) frameProps['_mount_color_inner'] = state.mountColorInnerHandle;
        if (sizeLabel)    frameProps['Size']    = sizeLabel;
        if (frameLabel)   frameProps['Frame']   = frameLabel;
        if (mountLabel)   frameProps['Mount']   = mountLabel;
        if (glazingLabel) frameProps['Glazing'] = glazingLabel;

        const res = await fetch('/cart/add.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            items: [
              { id: artVariantId,   quantity: 1, properties: artProps },
              { id: state.variantId, quantity: 1, properties: frameProps },
            ],
          }),
        });

        if (!res.ok) {
          const txt = await res.text().catch(() => '');
          throw new Error(`cart/add ${res.status}: ${txt.slice(0, 120)}`);
        }

        const cartData = await res.json();
        document.dispatchEvent(new CustomEvent('cart:update', {
          bubbles: true,
          composed: true,
          detail: {
            resource: cartData,
            data: {
              source: 'readymade-frame-pdp',
              productId: String(data.productId || ''),
              variantId: String(state.variantId || ''),
              itemCount: 2,
              price: cartData.final_price ?? cartData.price ?? null,
              currency: cartData.currency ?? window.Shopify?.currency?.active ?? null,
            },
          },
        }));

      } catch (err) {
        console.error('[RMF] framed print ATC failed', err);
      } finally {
        buyFrameBtn.disabled = false;
        if (labelEl) labelEl.textContent = savedLabel;
      }
    });
  }

  // No-photo ATC: intercept native form POST so we open the cart drawer instead
  // of redirecting to /cart. Runs after the photo handler; e.defaultPrevented
  // is true when the photo path already took over, so we bail in that case.
  if (frameForm) {
    frameForm.addEventListener('submit', async (e) => {
      if (e.defaultPrevented) return;
      e.preventDefault();

      const btn = document.getElementById('rmf-buy-frame-btn');
      const labelEl = btn?.querySelector('.rmf-btn__label');
      const savedLabel = labelEl?.textContent || '';
      if (btn) btn.disabled = true;
      if (labelEl) labelEl.textContent = 'Adding…';

      try {
        const res = await fetch('/cart/add.js', {
          method: 'POST',
          body: new FormData(frameForm),
        });
        if (!res.ok) {
          const txt = await res.text().catch(() => '');
          throw new Error(`cart/add ${res.status}: ${txt.slice(0, 120)}`);
        }
        const cartData = await res.json();
        document.dispatchEvent(new CustomEvent('cart:update', {
          bubbles: true,
          composed: true,
          detail: {
            resource: cartData,
            data: {
              source: 'readymade-frame-pdp',
              productId: String(data.productId || ''),
              variantId: String(state.variantId || ''),
              itemCount: 1,
              price: cartData.final_price ?? cartData.price ?? null,
              currency: cartData.currency ?? window.Shopify?.currency?.active ?? null,
            },
          },
        }));
      } catch (err) {
        console.error('[RMF] frame ATC failed', err);
      } finally {
        if (btn) btn.disabled = false;
        if (labelEl) labelEl.textContent = savedLabel;
      }
    });
  }

  // ── Init ──────────────────────────────────────────────────────────────────

  if (!isTierMode) {
    const urlVariant = new URLSearchParams(window.location.search).get('variant');
    if (urlVariant) {
      const id = Number(urlVariant);
      if (data.variants.find(v => v.id === id)) selectVariant(id);
    }
  }

  // Apply profile points from metaobject data (no API call needed).
  if (frameMock && data.profilePoints) {
    try {
      frameMock.profilePoints = typeof data.profilePoints === 'string'
        ? JSON.parse(data.profilePoints) : data.profilePoints;
      frameMock.scheduleRender?.();
    } catch (e) { /* malformed JSON — mock falls back to flat shading */ }
  }

  // Price and buy button were hidden until JS sets the correct tier price
  section.removeAttribute('data-tier-pending');

  // Restore persisted photo (runs after frame mock has initialised)
  restorePhotoFromStorage();
  // Also apply to range grid mocks which are server-rendered (similar grid applies on lazy load)
  applyPhotoToGridMocks();

  // ── Advisor apply-configuration bridge ────────────────────────────────────
  // The art-print PDP (style-picker.js) listens for `pdp:apply-configuration`
  // and updates its configurator in place when the agent commits a design.
  // RMF PDP didn't have an equivalent — agent applies dispatched here went
  // into the void. Map the event detail to the RMF PDP's setters:
  //   - frameColorHandle → selectFrameColor()
  //   - sizeHandle       → selectSizeOption()
  //   - borderOption     → click .rmf-mount-option[data-mount=...]
  // We dispatch synthetic clicks for axes that don't have callable setters
  // exposed (border, mount color, glazing all derive state via click handlers).
  document.addEventListener('pdp:apply-configuration', (ev) => {
    const d = ev.detail || {};
    try {
      if (d.frameColorHandle) selectFrameColor(d.frameColorHandle);
    } catch (err) { console.warn('[RMF] apply frameColor failed', err); }
    try {
      if (d.sizeHandle) selectSizeOption(d.sizeHandle);
    } catch (err) { console.warn('[RMF] apply size failed', err); }
    try {
      if (d.borderOption) {
        const btn = document.querySelector(`.rmf-mount-option[data-mount="${d.borderOption}"]`);
        if (btn) btn.click();
      }
    } catch (err) { console.warn('[RMF] apply border failed', err); }
    try {
      if (d.mountColorHandle) {
        const btn = document.querySelector(`[data-mount-color="${d.mountColorHandle}"], [data-mount-color-handle="${d.mountColorHandle}"]`);
        if (btn) btn.click();
      }
    } catch (err) { console.warn('[RMF] apply mountColor failed', err); }
    try {
      if (d.glazingHandle) {
        const btn = document.querySelector(`[data-glazing="${d.glazingHandle}"], [data-glazing-handle="${d.glazingHandle}"]`);
        if (btn) btn.click();
      }
    } catch (err) { console.warn('[RMF] apply glazing failed', err); }
  });

})();
